export type MarketingLanguage = "tr" | "en" | "ru" | "de" | "fr" | "it";

export interface MarketingFeature {
  title: string;
  description: string;
}

export interface MarketingStat {
  value: string;
  label: string;
}

export interface MarketingPlan {
  name: string;
  price: string;
  description: string;
  features: string[];
  popular?: boolean;
}

export interface MarketingPost {
  title: string;
  excerpt: string;
  category: string;
  date: string;
}

export interface MarketingRole {
  title: string;
  location: string;
  type: string;
  team: string;
  description: string;
}

export interface MarketingFaq {
  question: string;
  answer: string;
}

export interface MarketingCopy {
  brand: {
    product: string;
    company: string;
  };
  nav: {
    features: string;
    blog: string;
    about: string;
    careers: string;
    helpCenter: string;
    signIn: string;
    getStarted: string;
  };
  landing: {
    hero: {
      badge: string;
      title: string;
      highlight: string;
      subtitle: string;
      primaryCta: string;
      secondaryCta: string;
    };
    proof: string[];
    previewLabel: string;
    stats: MarketingStat[];
    features: {
      title: string;
      subtitle: string;
      items: MarketingFeature[];
    };
    integrations: {
      title: string;
      subtitle: string;
      items: { name: string }[];
    };
    useCases: {
      title: string;
      items: MarketingFeature[];
    };
    enterprise: {
      badge: string;
      title: string;
      subtitle: string;
      highlights: MarketingFeature[];
      workflow: {
        title: string;
        steps: MarketingFeature[];
      };
      outcomes: MarketingFeature[];
    };
    pricing: {
      title: string;
      subtitle: string;
      popularLabel: string;
      perMonth: string;
      plans: MarketingPlan[];
    };
    cta: {
      title: string;
      subtitle: string;
      primary: string;
      secondary: string;
    };
    howItWorks: {
      title: string;
      subtitle: string;
      steps: { number: string; title: string; description: string }[];
    };
    spotlights: {
      badge: string;
      title: string;
      subtitle: string;
      items: string[];
    }[];
  };
  featuresPage: {
    hero: {
      title: string;
      subtitle: string;
      primaryCta: string;
      secondaryCta: string;
    };
    highlights: MarketingFeature[];
    capabilities: {
      title: string;
      subtitle: string;
    };
    trust: {
      title: string;
      items: MarketingFeature[];
    };
    cta: {
      title: string;
      subtitle: string;
      primary: string;
      secondary: string;
    };
  };
  blogPage: {
    hero: {
      title: string;
      subtitle: string;
    };
    categories: string[];
    posts: MarketingPost[];
    newsletter: {
      title: string;
      subtitle: string;
      placeholder: string;
      button: string;
    };
  };
  aboutPage: {
    hero: {
      title: string;
      subtitle: string;
    };
    mission: MarketingFeature;
    vision: MarketingFeature;
    story: {
      title: string;
      steps: { title: string; description: string }[];
    };
    values: {
      title: string;
      items: MarketingFeature[];
    };
    leadership: {
      title: string;
      people: { name: string; role: string }[];
    };
    stats: MarketingStat[];
    cta: {
      title: string;
      subtitle: string;
      primary: string;
      secondary: string;
    };
  };
  careersPage: {
    hero: {
      title: string;
      subtitle: string;
      primaryCta: string;
      secondaryCta: string;
    };
    benefits: {
      title: string;
      items: string[];
    };
    roles: {
      title: string;
      items: MarketingRole[];
    };
    process: {
      title: string;
      steps: { title: string; description: string }[];
    };
    cta: {
      title: string;
      subtitle: string;
      primary: string;
      secondary: string;
    };
  };
  helpPage: {
    hero: {
      title: string;
      subtitle: string;
    };
    categories: {
      title: string;
      items: MarketingFeature[];
    };
    articles: {
      title: string;
      items: string[];
    };
    faqs: {
      title: string;
      items: MarketingFaq[];
    };
    contact: {
      title: string;
      subtitle: string;
      primary: string;
      secondary: string;
    };
  };
  footer: {
    tagline: string;
    product: string;
    company: string;
    support: string;
    links: {
      features: string;
      blog: string;
      about: string;
      careers: string;
      helpCenter: string;
      pricing: string;
    };
    rights: string;
  };
}

export const languageOptions: { value: MarketingLanguage; label: string }[] = [
  { value: "tr", label: "Türkçe" },
  { value: "en", label: "English" },
  { value: "ru", label: "Русский" },
  { value: "de", label: "Deutsch" },
  { value: "fr", label: "Français" },
  { value: "it", label: "Italiano" }
];

export const marketingCopy: Record<MarketingLanguage, MarketingCopy> = {
  en: {
    brand: {
      product: "Hümo",
      company: "Hümo"
    },
    nav: {
      features: "Features",
      blog: "Blog",
      about: "About",
      careers: "Careers",
      helpCenter: "Help Center",
      signIn: "Sign In",
      getStarted: "Get Started"
    },
    landing: {
      hero: {
        badge: "AI-Powered Sales Intelligence Platform",
        title: "Convert every conversation",
        highlight: "into revenue",
        subtitle:
          "Hümo gives your team real-time AI coaching, auto-replies powered by your knowledge base, automatic CSAT collection, and smart follow-ups — all in one inbox.",
        primaryCta: "Start free",
        secondaryCta: "See How It Works"
      },
      proof: ["No credit card required", "Setup in 5 minutes", "Cancel anytime"],
      previewLabel: "Live Dashboard",
      stats: [
        { value: "24/7", label: "Instant replies" },
        { value: "TR + EN", label: "In your customer's language" },
        { value: "3 channels", label: "WhatsApp · Instagram · Web" }
      ],
      features: {
        title: "Every tool your team needs to sell and support",
        subtitle:
          "From AI-powered auto-replies to real-time coaching, CSAT, and scheduled follow-ups — Hümo turns your inbox into a revenue machine.",
        items: [
          {
            title: "AI Copilot",
            description:
              "Ready-to-send message suggestions surface above the composer. Your agents click, edit, or send instantly — moving faster without sacrificing quality."
          },
          {
            title: "Knowledge Base AI",
            description:
              "Upload your FAQs, policies, and product docs. Hümo auto-replies from your own content with semantic search and confidence scoring."
          },
          {
            title: "CSAT & Satisfaction",
            description:
              "Automatically collect star ratings and comments after every conversation. Spot trends, identify weak spots, and coach with real customer data."
          },
          {
            title: "Scheduled Messages",
            description:
              "Schedule follow-ups, set inactivity triggers, and send proactive reminders. Never let a warm lead go cold again."
          },
          {
            title: "Sales Intelligence",
            description: "Track WON/LOST outcomes, spot missed opportunities, and coach agents with full analytics. See your conversion funnel from first message to closed deal."
          },
          {
            title: "Omnichannel Inbox",
            description: "Instagram, WhatsApp, Telegram, Web Chat — one unified inbox with AI analysis on every message. No tab-switching, no missed conversations."
          }
        ]
      },
      integrations: {
        title: "Connect all your global channels",
        subtitle: "One unified inbox for customers worldwide, in any language.",
        items: [
          { name: "Instagram DMs" },
          { name: "WhatsApp Business" },
          { name: "Web Chat Widget" },
          { name: "Voice Messages" }
        ]
      },
      useCases: {
        title: "Built for teams that sell",
        items: [
          {
            title: "E-commerce & DTC Brands",
            description:
              "Handle order inquiries, returns, and product questions with AI auto-replies. Convert browsing customers into buyers with real-time coaching and follow-ups."
          },
          {
            title: "Sales Teams",
            description:
              "AI coaches agents on every conversation, suggests next best actions, and tracks conversion outcomes. Close more deals with less effort."
          },
          {
            title: "Customer Support",
            description:
              "Serve customers in multiple languages without hiring translators. Auto-replies handle routine questions while agents focus on high-value conversations."
          }
        ]
      },
      enterprise: {
        badge: "Enterprise Voice Intelligence",
        title: "Turn recorded calls into revenue insights",
        subtitle:
          "Bulk upload call recordings, auto-transcribe conversations, then analyze mistakes, missed sales, and the phrases that persuaded customers.",
        highlights: [
          {
            title: "Bulk audio ingestion",
            description: "Upload batches of call recordings and process them automatically."
          },
          {
            title: "Voice transcription",
            description: "Detect agent vs customer and align every turn."
          },
          {
            title: "Sales quality analysis",
            description: "Spot mistakes, missed opportunities, and winning moments."
          },
          {
            title: "Agent reporting",
            description: "Generate scorecards and coaching-ready performance reports."
          }
        ],
        workflow: {
          title: "How it works",
          steps: [
            {
              title: "Import recordings",
              description: "Bring in multiple files at once or sync from storage."
            },
            {
              title: "Transcribe & tag",
              description: "Whisper transcribes and assigns agent/customer roles."
            },
            {
              title: "Analyze the conversation",
              description: "AI highlights risks, missed sales, and persuasion lines."
            },
            {
              title: "Report and coach",
              description: "Deliver reports and action plans for every rep."
            }
          ]
        },
        outcomes: [
          {
            title: "Quality scorecards",
            description: "Standardize review across teams."
          },
          {
            title: "Missed-sale alerts",
            description: "See exactly where deals slipped."
          },
          {
            title: "Winning playbooks",
            description: "Capture phrases that convert."
          }
        ]
      },
      pricing: {
        title: "Simple, transparent pricing",
        subtitle: "Start free, upgrade as you grow.",
        popularLabel: "Most Popular",
        perMonth: "/month",
        plans: [
          {
            name: "Free",
            price: "$0",
            description: "Perfect for trying out Hümo",
            features: [
              "50 conversations/month",
              "Basic AI analysis",
              "1 channel integration",
              "Email support"
            ]
          },
          {
            name: "Starter",
            price: "$29",
            description: "For small teams getting started",
            popular: true,
            features: [
              "200 conversations/month",
              "Advanced AI analysis",
              "3 channel integrations",
              "Auto-reply suggestions",
              "Priority support"
            ]
          },
          {
            name: "Pro",
            price: "$99",
            description: "For growing businesses",
            features: [
              "1,000 conversations/month",
              "Real-time AI coach",
              "Unlimited integrations",
              "Auto-reply automation",
              "Sales intelligence",
              "Analytics dashboard",
              "24/7 support"
            ]
          },
          {
            name: "Growth",
            price: "Contact us",
            description: "For scaling enterprises",
            features: [
              "Unlimited messages",
              "Custom AI training",
              "White-label options",
              "API access",
              "Dedicated account manager",
              "SLA guarantee"
            ]
          }
        ]
      },
      cta: {
        title: "Ready to turn conversations into revenue?",
        subtitle: "Join growing teams using Hümo to close more deals, faster.",
        primary: "Start free",
        secondary: "Schedule Demo"
      },
      howItWorks: {
        title: "Up and running in minutes",
        subtitle: "Connect your channels, add your knowledge base, and let Hümo do the rest.",
        steps: [
          {
            number: "01",
            title: "Connect your channels",
            description: "Link Instagram, WhatsApp, Telegram, or embed the chat widget — takes less than 5 minutes."
          },
          {
            number: "02",
            title: "Add your knowledge base",
            description: "Upload FAQs, policies, and product docs. Hümo learns your business and auto-replies with confidence."
          },
          {
            number: "03",
            title: "AI handles the heavy lifting",
            description: "Auto-replies, coaching, follow-ups, CSAT — Hümo runs your inbox so your team can focus on closing."
          }
        ]
      },
      spotlights: [
        {
          badge: "AI Copilot",
          title: "Click-to-send suggestions, always one step ahead",
          subtitle: "Hümo's AI Copilot analyzes each conversation and surfaces ready-to-send message chips above your composer. Your team moves faster without sacrificing quality.",
          items: [
            "Real-time message suggestions based on conversation context",
            "Click to insert, edit, or send with a single click",
            "Learns from your best agents' reply patterns",
            "Works on every conversation, every channel"
          ]
        },
        {
          badge: "CSAT & Satisfaction",
          title: "Measure what customers really think",
          subtitle: "Automatically collect star ratings and comments after every conversation. Spot trends, identify weak spots, and coach your team with real customer data — not guesses.",
          items: [
            "Automatic CSAT survey after conversation close",
            "Star rating + optional comment collection",
            "Real-time satisfaction dashboard with trends",
            "Filter by agent, channel, and time period"
          ]
        },
        {
          badge: "Scheduled Messages",
          title: "Never let a warm lead go cold",
          subtitle: "Schedule follow-up messages, set inactivity triggers, and send proactive reminders. Hümo follows up automatically so your team never misses a lead.",
          items: [
            "Schedule messages for the perfect moment",
            "Auto-trigger when customer goes inactive",
            "Follow-up reminder workflows",
            "Works across all connected channels"
          ]
        }
      ]
    },
    featuresPage: {
      hero: {
        title: "All the features your team needs to sell and support at scale",
        subtitle:
          "From AI analysis to automation and analytics, Hümo gives you the tools to move faster with confidence.",
        primaryCta: "Start free",
        secondaryCta: "View Pricing"
      },
      highlights: [
        {
          title: "Intent & Sentiment",
          description: "Understand why customers write and how to respond with the right tone."
        },
        {
          title: "Revenue Analytics",
          description: "See conversion impact by channel, agent, and AI automation."
        }
      ],
      capabilities: {
        title: "Platform capabilities",
        subtitle: "Everything from onboarding to reporting is designed for busy teams."
      },
      trust: {
        title: "Trust and security",
        items: [
          { title: "GDPR aligned", description: "Privacy-first by design with clear data controls." },
          { title: "Admin and agent roles", description: "Separate permissions for admins and agents." },
          { title: "Encryption in transit", description: "Connections use HTTPS; channel credentials are stored encrypted." }
        ]
      },
      cta: {
        title: "See Hümo in action",
        subtitle: "Let Hümo help you launch in days, not months.",
        primary: "Get Started",
        secondary: "Contact Sales"
      }
    },
    blogPage: {
      hero: {
        title: "Insights from Hümo",
        subtitle: "Product updates, AI research, and best practices for modern teams."
      },
      categories: ["All", "Product", "AI", "Sales", "CX"],
      posts: [
        {
          title: "How AI changes customer conversations",
          excerpt: "A practical look at intent detection, coaching, and safer automation.",
          category: "AI",
          date: "Jan 10, 2026"
        },
        {
          title: "From reply speed to revenue",
          excerpt: "The metrics that actually move pipeline and retention.",
          category: "Sales",
          date: "Jan 4, 2026"
        },
        {
          title: "Building trust in automated replies",
          excerpt: "Why guardrails, human review, and transparency matter.",
          category: "CX",
          date: "Dec 20, 2025"
        }
      ],
      newsletter: {
        title: "Get the latest updates",
        subtitle: "Monthly product news and AI playbooks in your inbox.",
        placeholder: "you@company.com",
        button: "Subscribe"
      }
    },
    aboutPage: {
      hero: {
        title: "About Hümo",
        subtitle: "We build Hümo to help teams deliver faster, more human customer experiences."
      },
      mission: {
        title: "Mission",
        description: "Make every customer conversation a growth opportunity with trusted AI."
      },
      vision: {
        title: "Vision",
        description: "A world where every team can scale care and revenue without sacrificing quality."
      },
      story: {
        title: "Our story",
        steps: [
          {
            title: "2019 — The idea",
            description: "We started as a research lab exploring intent detection for real businesses."
          },
          {
            title: "2022 — Hümo launches",
            description: "Early customers cut response times in half with AI coaching."
          },
          {
            title: "2026 — Global teams",
            description: "Hümo now supports teams across Europe and the Middle East."
          }
        ]
      },
      values: {
        title: "Our values",
        items: [
          { title: "Customer obsession", description: "We design from the customer backward." },
          { title: "Trust by default", description: "Security and transparency are non-negotiable." },
          { title: "Speed with care", description: "Move fast without breaking the human experience." }
        ]
      },
      leadership: {
        title: "Leadership",
        people: []
      },
      stats: [
        { value: "24/7", label: "Instant replies" },
        { value: "TR + EN", label: "In your customer's language" },
        { value: "3 channels", label: "WhatsApp · Instagram · Web" }
      ],
      cta: {
        title: "Partner with Hümo",
        subtitle: "Let’s build the next generation of customer conversations together.",
        primary: "Contact Us",
        secondary: "View Careers"
      }
    },
    careersPage: {
      hero: {
        title: "Join Hümo",
        subtitle: "Build AI products that help teams deliver better customer experiences.",
        primaryCta: "See Open Roles",
        secondaryCta: "Email Us"
      },
      benefits: {
        title: "Why you’ll love working here",
        items: [
          "Remote-first with flexible hours",
          "Competitive salary and equity",
          "Learning and conference budget",
          "High-impact, product-led culture",
          "Modern tools and supportive teams"
        ]
      },
      roles: {
        title: "Open roles",
        items: [
          {
            title: "Frontend Engineer",
            location: "Remote · Europe",
            type: "Full-time",
            team: "Product",
            description: "Build fast, delightful UI experiences for Hümo customers."
          },
          {
            title: "Backend Engineer",
            location: "Remote · Turkey",
            type: "Full-time",
            team: "Platform",
            description: "Design scalable systems for message processing and analytics."
          },
          {
            title: "Machine Learning Engineer",
            location: "Remote · Europe",
            type: "Full-time",
            team: "AI",
            description: "Improve classification, coaching, and safe automation models."
          },
          {
            title: "Customer Success Manager",
            location: "Hybrid · Istanbul",
            type: "Full-time",
            team: "Customer",
            description: "Help teams launch quickly and get measurable impact."
          }
        ]
      },
      process: {
        title: "Hiring process",
        steps: [
          { title: "Intro call", description: "Meet the team and align on expectations." },
          { title: "Role interview", description: "Deep dive into your craft and real scenarios." },
          { title: "Final chat", description: "Culture fit, offer, and next steps." }
        ]
      },
      cta: {
        title: "Ready to apply?",
        subtitle: "We reply to every application within five business days.",
        primary: "Apply Now",
        secondary: "careers@meethumo.com"
      }
    },
    helpPage: {
      hero: {
        title: "Help Center",
        subtitle: "Find answers, tutorials, and best practices for Hümo."
      },
      categories: {
        title: "Browse by topic",
        items: [
          { title: "Getting started", description: "Set up Hümo and invite your team." },
          { title: "Integrations", description: "Connect Instagram, WhatsApp, and web chat." },
          { title: "Automation", description: "Build auto-replies and routing rules." },
          { title: "Analytics", description: "Track conversions and response metrics." },
          { title: "Billing", description: "Plans, invoices, and usage details." }
        ]
      },
      articles: {
        title: "Popular articles",
        items: [
          "Connect WhatsApp in 5 minutes",
          "Set confidence thresholds for auto-reply",
          "Create and manage saved templates",
          "Export analytics reports"
        ]
      },
      faqs: {
        title: "Frequently asked questions",
        items: [
          {
            question: "Does Hümo replace my agents?",
            answer: "No. Hümo assists your team with analysis and suggested replies so agents can respond faster."
          },
          {
            question: "Can I control what gets auto-sent?",
            answer: "Yes. You can set confidence thresholds and require human review when needed."
          },
          {
            question: "Which channels are supported?",
            answer: "Instagram, WhatsApp, web chat, and voice are supported today, with more coming soon."
          },
          {
            question: "How quickly can we go live?",
            answer: "Most teams are live in under a week with onboarding from Hümo."
          }
        ]
      },
      contact: {
        title: "Still need help?",
        subtitle: "Our support team is here for you in English and Turkish.",
        primary: "Contact Support",
        secondary: "support@meethumo.com"
      }
    },
    footer: {
      tagline: "AI-powered customer intelligence platform for modern teams.",
      product: "Product",
      company: "Company",
      support: "Support",
      links: {
        features: "Features",
        blog: "Blog",
        about: "About",
        careers: "Careers",
        helpCenter: "Help Center",
        pricing: "Pricing"
      },
      rights: "© 2026 Hümo. All rights reserved."
    }
  },
  tr: {
    brand: {
      product: "Hümo",
      company: "Hümo"
    },
    nav: {
      features: "Özellikler",
      blog: "Blog",
      about: "Hakkımızda",
      careers: "Kariyer",
      helpCenter: "Yardım Merkezi",
      signIn: "Giriş",
      getStarted: "Başla"
    },
    landing: {
      hero: {
        badge: "AI Destekli Satış Zekası Platformu",
        title: "Her konuşmayı",
        highlight: "satışa dönüştürün",
        subtitle:
          "Hümo ekibinize gerçek zamanlı AI koçluğu, bilgi tabanından desteklenen otomatik yanıtlar, otomatik CSAT toplama ve akıllı takip mesajları sunar — hepsi tek gelen kutusunda.",
        primaryCta: "Ücretsiz Başlayın",
        secondaryCta: "Nasıl Çalışır?"
      },
      proof: ["Kredi kartı gerekmez", "5 dakikada kurulum", "İstediğinizde iptal"],
      previewLabel: "Canlı Dashboard",
      stats: [
        { value: "7/24", label: "Anında cevap" },
        { value: "TR + EN", label: "Müşterinin dilinde" },
        { value: "3 kanal", label: "WhatsApp · Instagram · Web" }
      ],
      features: {
        title: "Satmak ve destek vermek için gereken her araç",
        subtitle: "AI destekli otomatik yanıtlardan gerçek zamanlı koçluğa, CSAT'a ve zamanlanmış takip mesajlarına kadar — Hümo gelen kutunuzu gelir makinesine dönüştürür.",
        items: [
          {
            title: "AI Copilot",
            description: "Mesaj alanının üstünde göndermeye hazır öneriler belirir. Temsilcileriniz tıklar, düzenler veya anında gönderir — kaliteden ödün vermeden daha hızlı."
          },
          {
            title: "Bilgi Tabanı AI",
            description: "SSS, politikalar ve ürün belgelerinizi yükleyin. Hümo kendi içeriğinizden semantik arama ve güven puanıyla otomatik yanıt verir."
          },
          {
            title: "CSAT ve Memnuniyet",
            description: "Her konuşma sonrası otomatik yıldız puanı ve yorum toplayın. Trendleri görün, zayıf noktaları belirleyin ve gerçek verilerle koçluk yapın."
          },
          {
            title: "Zamanlanmış Mesajlar",
            description: "Takip mesajları zamanlayın, hareketsizlik tetikleyicileri ayarlayın ve proaktif hatırlatmalar gönderin. Hiçbir sıcak lead soğumasın."
          },
          {
            title: "Satış Zekası",
            description: "KAZANILDI/KAYBEDİLDİ sonuçlarını izleyin, kaçan fırsatları görün ve tam analitik ile temsilcileri geliştirin. İlk mesajdan kapanışa dönüşüm hunisini takip edin."
          },
          {
            title: "Omnichannel Inbox",
            description: "Instagram, WhatsApp, Telegram, Web Chat — her mesajda AI analiziyle tek birleşik inbox. Sekme değiştirmeye son, hiç kaçan konuşma olmaz."
          }
        ]
      },
      integrations: {
        title: "Tüm global kanallarınızı bağlayın",
        subtitle: "Dünya çapındaki müşteriler için herhangi bir dilde tek birleşik inbox.",
        items: [
          { name: "Instagram DM" },
          { name: "WhatsApp Business" },
          { name: "Web Chat Widget" },
          { name: "Ses Mesajları" }
        ]
      },
      useCases: {
        title: "Satan ekipler için tasarlandı",
        items: [
          {
            title: "E-ticaret ve DTC Markalar",
            description: "Sipariş sorularını, iadeleri ve ürün merakını AI otomatik yanıtlarla karşılayın. Gezinen müşteriyi alıcıya dönüştürmek için gerçek zamanlı koçluk ve takip mesajları."
          },
          {
            title: "Satış Ekipleri",
            description: "AI her konuşmada temsilcilere koçluk yapar, en iyi hamleri önerir ve dönüşüm sonuçlarını izler. Daha az eforla daha fazla anlaşma kapatın."
          },
          {
            title: "Müşteri Desteği",
            description: "Birden fazla dilde çevirmen tutmadan hizmet verin. Otomatik yanıtlar rutin soruları karşılar, temsilciler değerli konuşmalara odaklanır."
          }
        ]
      },
      enterprise: {
        badge: "Kurumsal Ses Zekası",
        title: "Kayıtlı satış görüşmelerini aksiyona dönüştürün",
        subtitle:
          "Görüşme kayıtlarını toplu yükleyin, otomatik transkript ile tüm konuşmayı analiz edin.",
        highlights: [
          {
            title: "Toplu ses yükleme",
            description: "Birden fazla görüşmeyi tek seferde içeri alın."
          },
          {
            title: "Ses transkripti",
            description: "Temsilci ve müşteri konuşmalarını otomatik etiketleyin."
          },
          {
            title: "Satış kalite analizi",
            description: "Hata, kaçan fırsat ve ikna anlarını belirleyin."
          },
          {
            title: "Satışçı raporu",
            description: "Performans skorları ve koçluk önerilerini paylaşın."
          }
        ],
        workflow: {
          title: "Nasıl çalışır?",
          steps: [
            {
              title: "Kayıtları içeri alın",
              description: "Toplu yükleme ile görüşmeleri sıraya alın."
            },
            {
              title: "Transkript & rol atama",
              description: "Whisper konuşmaları yazıya döker, müşteri/temsilciyi ayırır."
            },
            {
              title: "Konuşmayı analiz edin",
              description: "Hatalar, kaçan satışlar ve ikna cümleleri öne çıkar."
            },
            {
              title: "Raporlayın ve geliştirin",
              description: "Her temsilci için performans ve aksiyon raporu."
            }
          ]
        },
        outcomes: [
          {
            title: "Kalite skor kartları",
            description: "Temsilci bazlı standart değerlendirme."
          },
          {
            title: "Kaçan satış uyarıları",
            description: "Fırsat kaçışlarını hızlı görün."
          },
          {
            title: "Kazandıran cümleler",
            description: "Dönüşüm getiren ifadeleri yakalayın."
          }
        ]
      },
      pricing: {
        title: "Basit ve şeffaf fiyatlandırma",
        subtitle: "Ücretsiz başlayın, büyüdükçe yükseltin.",
        popularLabel: "En Popüler",
        perMonth: "/ay",
        plans: [
          {
            name: "Ücretsiz",
            price: "$0",
            description: "Hümo'yu denemek için ideal",
            features: [
              "Aylık 100 mesaj",
              "Temel AI analizi",
              "1 kanal entegrasyonu",
              "E-posta desteği"
            ]
          },
          {
            name: "Başlangıç",
            price: "$29",
            description: "Küçük ekipler için",
            popular: true,
            features: [
              "Aylık 1.000 mesaj",
              "Gelişmiş AI analizi",
              "3 kanal entegrasyonu",
              "Otomatik yanıt önerileri",
              "Öncelikli destek"
            ]
          },
          {
            name: "Pro",
            price: "$99",
            description: "Büyüyen işletmeler için",
            features: [
              "Aylık 10.000 mesaj",
              "Gerçek zamanlı AI koç",
              "Sınırsız entegrasyon",
              "Otomatik yanıt otomasyonu",
              "Satış zekası",
              "Analitik panel",
              "7/24 destek"
            ]
          },
          {
            name: "Büyüme",
            price: "İletişime geçin",
            description: "Kurumsal ölçek için",
            features: [
              "Sınırsız mesaj",
              "Özel AI eğitimi",
              "White-label seçenekler",
              "API erişimi",
              "Özel müşteri yöneticisi",
              "SLA garantisi"
            ]
          }
        ]
      },
      cta: {
        title: "Konuşmalarınızı satışa dönüştürmeye hazır mısınız?",
        subtitle: "Daha fazla anlaşma kapatan büyüyen ekiplere katılın.",
        primary: "Ücretsiz Başlayın",
        secondary: "Demo Planlayın"
      },
      howItWorks: {
        title: "Dakikalar içinde hazır",
        subtitle: "Kanallarınızı bağlayın, bilgi tabanınızı ekleyin ve gerisini Hümo'ya bırakın.",
        steps: [
          {
            number: "01",
            title: "Kanallarınızı bağlayın",
            description: "Instagram, WhatsApp, Telegram veya web chat widget'ını 5 dakikadan kısa sürede bağlayın."
          },
          {
            number: "02",
            title: "Bilgi tabanınızı ekleyin",
            description: "SSS, politikalar ve ürün belgelerinizi yükleyin. Hümo işletmenizi öğrenir ve güvenle otomatik yanıt verir."
          },
          {
            number: "03",
            title: "AI ağır işi yapar",
            description: "Otomatik yanıtlar, koçluk, takip mesajları, CSAT — Hümo gelen kutunuzu yönetir, ekibiniz satışa odaklanır."
          }
        ]
      },
      spotlights: [
        {
          badge: "AI Copilot",
          title: "Tek tıkla göndermeye hazır öneriler",
          subtitle: "Hümo'nun AI Copilot'u her konuşmayı analiz ederek mesaj alanının üstünde hazır chip'ler gösterir. Ekibiniz kaliteyi düşürmeden daha hızlı çalışır.",
          items: [
            "Konuşma bağlamına göre gerçek zamanlı mesaj önerileri",
            "Tıkla, düzenle veya tek tıkla gönder",
            "En iyi temsilcilerinizin yanıt kalıplarından öğrenir",
            "Her kanalda, her konuşmada kullanılabilir"
          ]
        },
        {
          badge: "CSAT ve Memnuniyet",
          title: "Müşterilerin gerçekten ne düşündüğünü ölçün",
          subtitle: "Her konuşma kapandıktan sonra otomatik yıldız puanı ve yorum toplayın. Trendleri görün, zayıf noktaları belirleyin ve gerçek verilerle ekibinizi geliştirin.",
          items: [
            "Konuşma kapanınca otomatik CSAT anketi",
            "Yıldız puanı + isteğe bağlı yorum toplama",
            "Gerçek zamanlı memnuniyet dashboard'u ve trendler",
            "Temsilci, kanal ve tarihe göre filtrele"
          ]
        },
        {
          badge: "Zamanlanmış Mesajlar",
          title: "Hiçbir sıcak lead'i soğutmayın",
          subtitle: "Takip mesajları zamanlayın, hareketsizlik tetikleyicileri ayarlayın ve proaktif hatırlatmalar gönderin. Hümo otomatik takip yapar, hiçbir lead kaçmaz.",
          items: [
            "En doğru an için mesaj zamanlama",
            "Müşteri sessiz kalırsa otomatik tetikleyici",
            "Takip hatırlatma iş akışları",
            "Tüm bağlı kanallarda çalışır"
          ]
        }
      ]
    },
    featuresPage: {
      hero: {
        title: "Satış ve destek ekipleri için tüm özellikler",
        subtitle:
          "AI analizinden otomasyona ve analitiğe kadar Hümo, güvenle daha hızlı hareket etmenizi sağlar.",
        primaryCta: "Ücretsiz Deneyin",
        secondaryCta: "Fiyatları Gör"
      },
      highlights: [
        {
          title: "Niyet ve Duygu",
          description: "Müşterinin neden yazdığını anlayın ve doğru tonda yanıt verin."
        },
        {
          title: "Gelir Analitiği",
          description: "Kanal, temsilci ve otomasyon bazında dönüşüm etkisini görün."
        }
      ],
      capabilities: {
        title: "Platform yetenekleri",
        subtitle: "Yoğun ekipler için uçtan uca tasarlanmış özellikler."
      },
      trust: {
        title: "Güven ve güvenlik",
        items: [
          { title: "GDPR uyumlu", description: "Gizlilik ve kontrol odaklı tasarım." },
          { title: "Yönetici ve temsilci rolleri", description: "Yöneticiler ve temsilciler için ayrı yetkiler." },
          { title: "Aktarımda şifreleme", description: "Bağlantılar HTTPS ile şifrelenir; kanal anahtarları şifreli saklanır." }
        ]
      },
      cta: {
        title: "Hümo'yu canlı görün",
        subtitle: "Hümo ile günler içinde canlıya geçin.",
        primary: "Başlayın",
        secondary: "Satışla Görüş"
      }
    },
    blogPage: {
      hero: {
        title: "Hümo'den İçgörüler",
        subtitle: "Ürün güncellemeleri, AI araştırmaları ve modern ekipler için rehberler."
      },
      categories: ["Tümü", "Ürün", "Yapay Zeka", "Satış", "Müşteri Deneyimi"],
      posts: [
        {
          title: "AI müşteri konuşmalarını nasıl değiştiriyor",
          excerpt: "Niyet tespiti, koçluk ve güvenli otomasyonun pratik kullanımı.",
          category: "Yapay Zeka",
          date: "10 Oca 2026"
        },
        {
          title: "Yanıt hızından gelire",
          excerpt: "Gelir ve sadakati etkileyen metrikler.",
          category: "Satış",
          date: "4 Oca 2026"
        },
        {
          title: "Otomatik yanıtlarda güven oluşturmak",
          excerpt: "Neden güvenlik katmanları ve insan kontrolü önemlidir.",
          category: "Müşteri Deneyimi",
          date: "20 Ara 2025"
        }
      ],
      newsletter: {
        title: "Güncellemeleri kaçırmayın",
        subtitle: "Aylık ürün haberleri ve AI rehberleri gelen kutunuzda.",
        placeholder: "siz@firma.com",
        button: "Abone Ol"
      }
    },
    aboutPage: {
      hero: {
        title: "Hümo Hakkında",
        subtitle: "Hümo ile ekiplerin daha hızlı ve insancıl deneyimler sunmasını sağlıyoruz."
      },
      mission: {
        title: "Misyon",
        description: "Her müşteri konuşmasını güvenilir AI ile büyüme fırsatına dönüştürmek."
      },
      vision: {
        title: "Vizyon",
        description: "Her ekibin kaliteyi bozmadan ölçeklenebildiği bir dünya."
      },
      story: {
        title: "Hikayemiz",
        steps: [
          {
            title: "2019 — Fikir",
            description: "Gerçek işletmeler için niyet tespiti üzerine araştırmalarla başladık."
          },
          {
            title: "2022 — Hümo yayında",
            description: "İlk müşteriler yanıt sürelerini yarıya indirdi."
          },
          {
            title: "2026 — Küresel ekipler",
            description: "Hümo artık Avrupa ve Orta Doğu'da ekipleri destekliyor."
          }
        ]
      },
      values: {
        title: "Değerlerimiz",
        items: [
          { title: "Müşteri odağı", description: "Tasarımı müşteriden başlatırız." },
          { title: "Güven", description: "Güvenlik ve şeffaflık vazgeçilmezdir." },
          { title: "Hız ve özen", description: "İnsan deneyimini bozmadan hızlı hareket ederiz." }
        ]
      },
      leadership: {
        title: "Liderlik",
        people: []
      },
      stats: [
        { value: "7/24", label: "Anında cevap" },
        { value: "TR + EN", label: "Müşterinin dilinde" },
        { value: "3 kanal", label: "WhatsApp · Instagram · Web" }
      ],
      cta: {
        title: "Hümo ile iş ortaklığı",
        subtitle: "Müşteri konuşmalarının yeni neslini birlikte inşa edelim.",
        primary: "Bize Ulaşın",
        secondary: "Kariyerleri Gör"
      }
    },
    careersPage: {
      hero: {
        title: "Hümo'ye Katılın",
        subtitle: "Ekiplerin daha iyi müşteri deneyimi sunmasına yardımcı olacak ürünler geliştirin.",
        primaryCta: "Açık Pozisyonlar",
        secondaryCta: "E-posta Gönder"
      },
      benefits: {
        title: "Neden burada çalışmak güzel",
        items: [
          "Esnek saatlerle remote çalışma",
          "Rekabetçi maaş ve hisse",
          "Eğitim ve konferans bütçesi",
          "Yüksek etkili ürün kültürü",
          "Modern araçlar ve destekleyici ekipler"
        ]
      },
      roles: {
        title: "Açık roller",
        items: [
          {
            title: "Frontend Mühendisi",
            location: "Remote · Avrupa",
            type: "Tam zamanlı",
            team: "Ürün",
            description: "Hümo için hızlı ve etkileyici arayüzler geliştirin."
          },
          {
            title: "Backend Mühendisi",
            location: "Remote · Türkiye",
            type: "Tam zamanlı",
            team: "Platform",
            description: "Mesaj işleme ve analitik için ölçeklenebilir sistemler tasarlayın."
          },
          {
            title: "Makine Öğrenimi Mühendisi",
            location: "Remote · Avrupa",
            type: "Tam zamanlı",
            team: "AI",
            description: "Sınıflandırma, koçluk ve otomasyon modellerini geliştirin."
          },
          {
            title: "Müşteri Başarı Yöneticisi",
            location: "Hibrit · İstanbul",
            type: "Tam zamanlı",
            team: "Müşteri",
            description: "Ekiplerin hızlı başlangıç yapmasını ve sonuç almasını sağlayın."
          }
        ]
      },
      process: {
        title: "İşe alım süreci",
        steps: [
          { title: "Tanışma", description: "Ekip ile tanışın ve beklentileri netleştirin." },
          { title: "Rol görüşmesi", description: "Uzmanlığınızı ve senaryoları birlikte değerlendirelim." },
          { title: "Son görüşme", description: "Kültür uyumu, teklif ve sonraki adımlar." }
        ]
      },
      cta: {
        title: "Başvurmaya hazır mısınız?",
        subtitle: "Her başvuruya 5 iş günü içinde dönüş yapıyoruz.",
        primary: "Hemen Başvur",
        secondary: "careers@meethumo.com"
      }
    },
    helpPage: {
      hero: {
        title: "Yardım Merkezi",
        subtitle: "Hümo için yanıtlar, eğitimler ve en iyi uygulamalar."
      },
      categories: {
        title: "Konuya göre keşfedin",
        items: [
          { title: "Başlangıç", description: "Hümo'yu kurun ve ekibinizi davet edin." },
          { title: "Entegrasyonlar", description: "Instagram, WhatsApp ve web sohbeti bağlayın." },
          { title: "Otomasyon", description: "Otomatik yanıt ve yönlendirme kuralları oluşturun." },
          { title: "Analitik", description: "Dönüşüm ve yanıt metriklerini izleyin." },
          { title: "Faturalandırma", description: "Planlar, faturalar ve kullanım detayları." }
        ]
      },
      articles: {
        title: "Popüler makaleler",
        items: [
          "WhatsApp'ı 5 dakikada bağlayın",
          "Otomatik yanıt için güven eşiği belirleyin",
          "Şablonları oluşturun ve yönetin",
          "Analitik raporlarını dışa aktarın"
        ]
      },
      faqs: {
        title: "Sık sorulan sorular",
        items: [
          {
            question: "Hümo temsilcilerimin yerini alır mı?",
            answer: "Hayır. Hümo analiz ve önerilerle ekibinizi hızlandırır."
          },
          {
            question: "Otomatik gönderimi kontrol edebilir miyim?",
            answer: "Evet. Güven eşikleri ve insan onayı gereksinimleri belirleyebilirsiniz."
          },
          {
            question: "Hangi kanallar destekleniyor?",
            answer: "Instagram, WhatsApp, web sohbet ve ses şu anda destekleniyor."
          },
          {
            question: "Ne kadar hızlı canlıya geçeriz?",
            answer: "Çoğu ekip bir haftadan kısa sürede canlıya geçer."
          }
        ]
      },
      contact: {
        title: "Hâlâ yardıma mı ihtiyacınız var?",
        subtitle: "Destek ekibimiz Türkçe ve İngilizce size yardımcı olur.",
        primary: "Destekle İletişim",
        secondary: "support@meethumo.com"
      }
    },
    footer: {
      tagline: "Modern ekipler için AI destekli müşteri zekası platformu.",
      product: "Ürün",
      company: "Şirket",
      support: "Destek",
      links: {
        features: "Özellikler",
        blog: "Blog",
        about: "Hakkımızda",
        careers: "Kariyer",
        helpCenter: "Yardım Merkezi",
        pricing: "Fiyatlandırma"
      },
      rights: "© 2026 Hümo. Tüm hakları saklıdır."
    }
  },
  ru: {
    brand: {
      product: "Hümo",
      company: "Hümo"
    },
    nav: {
      features: "Функции",
      blog: "Блог",
      about: "О нас",
      careers: "Карьера",
      helpCenter: "Центр помощи",
      signIn: "Войти",
      getStarted: "Начать"
    },
    landing: {
      hero: {
        badge: "ИИ‑аналитика для клиентских сообщений",
        title: "Превращайте сообщения клиентов в",
        highlight: "Выручку",
        subtitle:
          "ИИ‑анализ, коучинг в реальном времени и умная автоматизация помогают командам закрывать больше сделок.",
        primaryCta: "Начать бесплатно",
        secondaryCta: "Смотреть демо"
      },
      proof: ["Без карты", "Бесплатный пробный период 14 дней", "Отмена в любое время"],
      previewLabel: "Предпросмотр панели",
      stats: [
        { value: "24/7", label: "Мгновенные ответы" },
        { value: "TR + EN", label: "На языке клиента" },
        { value: "3 канала", label: "WhatsApp · Instagram · Web" }
      ],
      features: {
        title: "Всё для масштабирования клиентских разговоров",
        subtitle: "Мощные функции ИИ помогают отвечать быстрее и продавать больше.",
        items: [
          {
            title: "ИИ‑анализ",
            description: "Автоматическая классификация по намерению, тону и срочности."
          },
          {
            title: "Умные автоответы",
            description: "Контекстные ответы и автоотправка с порогами уверенности."
          },
          {
            title: "Коучинг в реальном времени",
            description: "ИИ помогает агентам вести диалоги эффективнее."
          },
          {
            title: "Sales‑инсайты",
            description: "Отслеживайте стадию и намерение покупки для роста сделок."
          },
          {
            title: "Единый инбокс",
            description: "Instagram, WhatsApp, веб‑чат и голосовые сообщения в одном месте."
          },
          {
            title: "Аналитика",
            description: "Метрики конверсии, воронки и производительность агентов."
          }
        ]
      },
      integrations: {
        title: "Подключите все каналы",
        subtitle: "Единый инбокс для всех диалогов с клиентами.",
        items: [
          { name: "Instagram" },
          { name: "WhatsApp" },
          { name: "Веб‑чат" },
          { name: "Голос" }
        ]
      },
      useCases: {
        title: "Для сильных команд",
        items: [
          {
            title: "E‑commerce поддержка",
            description: "Автоматизируйте вопросы по заказам и возвратам."
          },
          {
            title: "Команды продаж",
            description: "Коучинг и сигналы намерения помогают закрывать сделки быстрее."
          },
          {
            title: "Customer Success",
            description: "Раннее выявление рисков и возможностей для upsell."
          }
        ]
      },
      enterprise: {
        badge: "Корпоративная голосовая аналитика",
        title: "Преобразуйте записи звонков в действия",
        subtitle:
          "Загружайте записи пачками, автоматически расшифровывайте и разделяйте роли, затем анализируйте ошибки, упущенные продажи и убедительные фразы.",
        highlights: [
          {
            title: "Пакетная загрузка аудио",
            description: "Загружайте партии записей и обрабатывайте автоматически."
          },
          {
            title: "Разделение спикеров",
            description: "Определяйте агента и клиента в каждой реплике."
          },
          {
            title: "Анализ качества продаж",
            description: "Выявляйте ошибки, упущенные возможности и сильные моменты."
          },
          {
            title: "Отчеты по агентам",
            description: "Формируйте скоркард и отчеты для коучинга."
          }
        ],
        workflow: {
          title: "Как это работает",
          steps: [
            {
              title: "Импорт записей",
              description: "Загрузите несколько файлов или синхронизируйте хранилище."
            },
            {
              title: "Транскрибация и теги",
              description: "Whisper переводит речь в текст и проставляет роли."
            },
            {
              title: "Анализ разговора",
              description: "ИИ подсвечивает риски, упущенные продажи и убедительные фразы."
            },
            {
              title: "Отчет и коучинг",
              description: "Передавайте отчеты и планы действий для каждого агента."
            }
          ]
        },
        outcomes: [
          {
            title: "Карты качества",
            description: "Единый стандарт оценки."
          },
          {
            title: "Сигналы упущенных сделок",
            description: "Сразу видно, где сделка сорвалась."
          },
          {
            title: "Плейбуки побед",
            description: "Собирайте фразы, которые конвертируют."
          }
        ]
      },
      pricing: {
        title: "Простые и прозрачные цены",
        subtitle: "Начните бесплатно и растите по мере роста.",
        popularLabel: "Самый популярный",
        perMonth: "/мес",
        plans: [
          {
            name: "Бесплатно",
            price: "$0",
            description: "Для знакомства с Hümo",
            features: [
              "100 сообщений/мес",
              "Базовый ИИ‑анализ",
              "1 интеграция",
              "Email‑поддержка"
            ]
          },
          {
            name: "Старт",
            price: "$29",
            description: "Для небольших команд",
            popular: true,
            features: [
              "1 000 сообщений/мес",
              "Продвинутый ИИ‑анализ",
              "3 интеграции",
              "Рекомендации автоответов",
              "Приоритетная поддержка"
            ]
          },
          {
            name: "Профи",
            price: "$99",
            description: "Для растущих компаний",
            features: [
              "10 000 сообщений/мес",
              "Коучинг в реальном времени",
              "Безлимитные интеграции",
              "Автоматизация ответов",
              "Sales‑инсайты",
              "Аналитическая панель",
              "Поддержка 24/7"
            ]
          },
          {
            name: "Рост",
            price: "Свяжитесь с нами",
            description: "Для масштабирования",
            features: [
              "Безлимитные сообщения",
              "Индивидуальное обучение ИИ",
              "White‑label опции",
              "Доступ к API",
              "Выделенный менеджер",
              "SLA‑гарантия"
            ]
          }
        ]
      },
      cta: {
        title: "Готовы преобразить работу с клиентами?",
        subtitle: "Присоединяйтесь к командам, которые растут с Hümo.",
        primary: "Начать бесплатно",
        secondary: "Запланировать демо"
      },
      howItWorks: {
        title: "Запустите за несколько минут",
        subtitle: "Подключите каналы, добавьте базу знаний — Hümo сделает остальное.",
        steps: [
          { number: "01", title: "Подключите каналы", description: "Instagram, WhatsApp, Telegram или веб‑виджет за 5 минут." },
          { number: "02", title: "Добавьте базу знаний", description: "Загрузите FAQ и документы — Hümo обучится и начнёт отвечать автоматически." },
          { number: "03", title: "ИИ берёт работу на себя", description: "Автоответы, коучинг, follow‑up, CSAT — Hümo управляет инбоксом, команда закрывает сделки." }
        ]
      },
      spotlights: [
        { badge: "AI Copilot", title: "Готовые ответы одним кликом", subtitle: "Hümo анализирует каждый диалог и предлагает сообщения прямо над полем ввода. Команда работает быстрее.", items: ["Предложения в реальном времени", "Один клик — отправить", "Учится у лучших агентов", "Работает во всех каналах"] },
        { badge: "CSAT", title: "Измеряйте, что думают клиенты", subtitle: "Автоматически собирайте оценки и комментарии после каждого разговора. Отслеживайте тренды и развивайте команду.", items: ["Автоопрос после закрытия чата", "Рейтинг + комментарий", "Дашборд в реальном времени", "Фильтр по агентам и каналам"] },
        { badge: "Scheduled Messages", title: "Не давайте лидам остывать", subtitle: "Планируйте follow‑up, настраивайте триггеры неактивности — Hümo следит за каждым лидом автоматически.", items: ["Запланированные сообщения", "Триггер по неактивности", "Напоминания follow‑up", "Работает во всех каналах"] }
      ]
    },
    featuresPage: {
      hero: {
        title: "Все функции для продаж и поддержки на масштабе",
        subtitle: "Hümo объединяет ИИ‑анализ, автоматизацию и аналитику в одной платформе.",
        primaryCta: "Начать бесплатно",
        secondaryCta: "Смотреть цены"
      },
      highlights: [
        {
          title: "Намерение и тон",
          description: "Понимайте мотивацию клиента и отвечайте правильным тоном."
        },
        {
          title: "Доходная аналитика",
          description: "Смотрите влияние ИИ по каналам, агентам и автоматизации."
        }
      ],
      capabilities: {
        title: "Возможности платформы",
        subtitle: "От внедрения до отчетности — всё для занятых команд."
      },
      trust: {
        title: "Надежность и безопасность",
        items: [
          { title: "GDPR", description: "Конфиденциальность и контроль данных по умолчанию." },
          { title: "Роли администратора и агента", description: "Отдельные права для администраторов и агентов." },
          { title: "Шифрование при передаче", description: "Соединения защищены HTTPS; ключи каналов хранятся в зашифрованном виде." }
        ]
      },
      cta: {
        title: "Посмотрите Hümo в деле",
        subtitle: "Hümo поможет запуститься за дни, а не месяцы.",
        primary: "Начать",
        secondary: "Связаться с продажами"
      }
    },
    blogPage: {
      hero: {
        title: "Идеи от Hümo",
        subtitle: "Обновления продукта, исследования ИИ и практики для команд."
      },
      categories: ["Все", "Продукт", "ИИ", "Продажи", "CX"],
      posts: [
        {
          title: "Как ИИ меняет клиентские разговоры",
          excerpt: "Практический взгляд на намерения, коучинг и безопасную автоматизацию.",
          category: "ИИ",
          date: "10 янв 2026"
        },
        {
          title: "От скорости ответа к выручке",
          excerpt: "Метрики, которые реально влияют на продажи и удержание.",
          category: "Продажи",
          date: "4 янв 2026"
        },
        {
          title: "Доверие к автоответам",
          excerpt: "Почему важны guardrails, контроль и прозрачность.",
          category: "CX",
          date: "20 дек 2025"
        }
      ],
      newsletter: {
        title: "Получайте обновления",
        subtitle: "Ежемесячные новости продукта и гайды по ИИ.",
        placeholder: "you@company.com",
        button: "Подписаться"
      }
    },
    aboutPage: {
      hero: {
        title: "О Hümo",
        subtitle: "Мы создаем Hümo, чтобы команды общались с клиентами быстрее и человечнее."
      },
      mission: {
        title: "Миссия",
        description: "Превращать каждую беседу с клиентом в возможность роста с помощью ИИ."
      },
      vision: {
        title: "Видение",
        description: "Мир, где команды масштабируют заботу и доход без потери качества."
      },
      story: {
        title: "Наша история",
        steps: [
          {
            title: "2019 — Идея",
            description: "Мы начали как исследовательская лаборатория по распознаванию намерений."
          },
          {
            title: "2022 — Запуск Hümo",
            description: "Первые клиенты сократили время ответа вдвое."
          },
          {
            title: "2026 — Международные команды",
            description: "Hümo поддерживает команды в Европе и на Ближнем Востоке."
          }
        ]
      },
      values: {
        title: "Наши ценности",
        items: [
          { title: "Фокус на клиента", description: "Проектируем от потребностей клиента." },
          { title: "Доверие", description: "Безопасность и прозрачность обязательны." },
          { title: "Скорость с заботой", description: "Двигаемся быстро, не теряя человечность." }
        ]
      },
      leadership: {
        title: "Руководство",
        people: []
      },
      stats: [
        { value: "24/7", label: "Мгновенные ответы" },
        { value: "TR + EN", label: "На языке клиента" },
        { value: "3 канала", label: "WhatsApp · Instagram · Web" }
      ],
      cta: {
        title: "Сотрудничайте с Hümo",
        subtitle: "Давайте построим новое поколение клиентских разговоров.",
        primary: "Связаться",
        secondary: "Карьера"
      }
    },
    careersPage: {
      hero: {
        title: "Присоединяйтесь к Hümo",
        subtitle: "Создавайте ИИ‑продукты для лучшего клиентского опыта.",
        primaryCta: "Открытые роли",
        secondaryCta: "Написать нам"
      },
      benefits: {
        title: "Почему стоит работать с нами",
        items: [
          "Удаленная работа и гибкий график",
          "Конкурентная компенсация",
          "Бюджет на обучение и конференции",
          "Культура высокого влияния",
          "Современные инструменты и поддержка"
        ]
      },
      roles: {
        title: "Открытые вакансии",
        items: [
          {
            title: "Frontend Engineer",
            location: "Remote · Europe",
            type: "Полная занятость",
            team: "Product",
            description: "Создавайте быстрые и удобные интерфейсы Hümo."
          },
          {
            title: "Backend Engineer",
            location: "Remote · Turkey",
            type: "Полная занятость",
            team: "Platform",
            description: "Проектируйте масштабируемые системы обработки сообщений."
          },
          {
            title: "Machine Learning Engineer",
            location: "Remote · Europe",
            type: "Полная занятость",
            team: "AI",
            description: "Улучшайте модели классификации и автоматизации."
          },
          {
            title: "Customer Success Manager",
            location: "Hybrid · Istanbul",
            type: "Полная занятость",
            team: "Customer",
            description: "Помогайте командам быстро запускаться и достигать результатов."
          }
        ]
      },
      process: {
        title: "Процесс найма",
        steps: [
          { title: "Звонок", description: "Знакомство и ожидания по роли." },
          { title: "Интервью", description: "Глубокий разбор навыков и кейсов." },
          { title: "Финал", description: "Культура, оффер и следующие шаги." }
        ]
      },
      cta: {
        title: "Готовы подать заявку?",
        subtitle: "Мы отвечаем на каждую заявку в течение 5 рабочих дней.",
        primary: "Подать заявку",
        secondary: "careers@meethumo.com"
      }
    },
    helpPage: {
      hero: {
        title: "Центр помощи",
        subtitle: "Ответы, туториалы и лучшие практики для Hümo."
      },
      categories: {
        title: "Темы",
        items: [
          { title: "Начало работы", description: "Настройте Hümo и пригласите команду." },
          { title: "Интеграции", description: "Подключите Instagram, WhatsApp и веб‑чат." },
          { title: "Автоматизация", description: "Создавайте автоответы и правила маршрутизации." },
          { title: "Аналитика", description: "Отслеживайте конверсии и скорость ответа." },
          { title: "Биллинг", description: "Тарифы, счета и использование." }
        ]
      },
      articles: {
        title: "Популярные статьи",
        items: [
          "Подключение WhatsApp за 5 минут",
          "Пороги уверенности для автоответов",
          "Шаблоны и их управление",
          "Экспорт отчетов"
        ]
      },
      faqs: {
        title: "Частые вопросы",
        items: [
          {
            question: "Hümo заменит моих агентов?",
            answer: "Нет. Hümo помогает анализом и рекомендациями для более быстрых ответов."
          },
          {
            question: "Можно ли контролировать автоотправку?",
            answer: "Да. Вы можете задать пороги уверенности и требовать проверки человеком."
          },
          {
            question: "Какие каналы поддерживаются?",
            answer: "Сейчас доступны Instagram, WhatsApp, веб‑чат и голосовые сообщения."
          },
          {
            question: "Сколько времени занимает запуск?",
            answer: "Большинство команд запускаются менее чем за неделю с помощью Hümo."
          }
        ]
      },
      contact: {
        title: "Нужна помощь?",
        subtitle: "Наша команда поддержки говорит по‑английски и по‑турецки.",
        primary: "Связаться с поддержкой",
        secondary: "support@meethumo.com"
      }
    },
    footer: {
      tagline: "Платформа клиентской аналитики на базе ИИ для современных команд.",
      product: "Продукт",
      company: "Компания",
      support: "Поддержка",
      links: {
        features: "Функции",
        blog: "Блог",
        about: "О нас",
        careers: "Карьера",
        helpCenter: "Центр помощи",
        pricing: "Цены"
      },
      rights: "© 2026 Hümo. Все права защищены."
    }
  },
  de: {
    brand: {
      product: "Hümo",
      company: "Hümo"
    },
    nav: {
      features: "Funktionen",
      blog: "Blog",
      about: "Über uns",
      careers: "Karriere",
      helpCenter: "Hilfe-Center",
      signIn: "Anmelden",
      getStarted: "Loslegen"
    },
    landing: {
      hero: {
        badge: "KI-gestützte Kundenintelligenz",
        title: "Verwandeln Sie Kundennachrichten in",
        highlight: "Umsatz",
        subtitle:
          "KI-Analyse, Coaching in Echtzeit und smarte Automatisierung helfen Teams, mehr Abschlüsse zu erzielen.",
        primaryCta: "Kostenlos starten",
        secondaryCta: "Demo ansehen"
      },
      proof: ["Keine Kreditkarte erforderlich", "14 Tage kostenlos testen", "Jederzeit kündbar"],
      previewLabel: "Dashboard-Vorschau",
      stats: [
        { value: "24/7", label: "Sofortige Antworten" },
        { value: "TR + EN", label: "In der Sprache Ihrer Kunden" },
        { value: "3 Kanäle", label: "WhatsApp · Instagram · Web" }
      ],
      features: {
        title: "Alles, um Kundenkonversationen zu skalieren",
        subtitle: "Leistungsstarke KI-Funktionen für schnellere Antworten und mehr Umsatz.",
        items: [
          {
            title: "KI-Analyse",
            description: "Automatische Klassifizierung nach Intention, Stimmung und Dringlichkeit."
          },
          {
            title: "Intelligente Auto-Antworten",
            description: "Kontextbezogene Antworten mit Vertrauensschwellen und Schutzmechanismen."
          },
          {
            title: "Echtzeit-Coach",
            description: "Live-Coaching für Agenten während der Konversation."
          },
          {
            title: "Sales Intelligence",
            description: "Kaufabsicht und Gesprächsphasen verfolgen, um Deals zu beschleunigen."
          },
          {
            title: "Omni-Channel Inbox",
            description: "Ein Posteingang für Instagram, WhatsApp, Web-Chat und Voice."
          },
          {
            title: "Analytics Dashboard",
            description: "Metriken, Funnels und Agenten-Performance im Blick."
          }
        ]
      },
      integrations: {
        title: "Alle Kanäle verbinden",
        subtitle: "Ein einheitlicher Posteingang für alle Kundenkonversationen.",
        items: [
          { name: "Instagram" },
          { name: "WhatsApp" },
          { name: "Web-Chat" },
          { name: "Voice" }
        ]
      },
      useCases: {
        title: "Für leistungsstarke Teams",
        items: [
          {
            title: "E-Commerce Support",
            description: "Bestellungen, Rückgaben und Anfragen automatisch bearbeiten."
          },
          {
            title: "Sales-Teams",
            description: "Coaching und Intent-Signale helfen beim schnelleren Abschluss."
          },
          {
            title: "Customer Success",
            description: "Risiken früh erkennen und Upsell-Chancen nutzen."
          }
        ]
      },
      enterprise: {
        badge: "Enterprise-Sprachanalyse",
        title: "Aufgenommene Verkaufsgespräche in Handlung verwandeln",
        subtitle:
          "Laden Sie Gesprächsaufzeichnungen gesammelt hoch, transkribieren Sie automatisch, trennen Sie Sprecher und analysieren Sie Fehler, verpasste Abschlüsse und überzeugende Aussagen.",
        highlights: [
          {
            title: "Batch-Audio-Import",
            description: "Aufnahmen in Stapeln hochladen und automatisch verarbeiten."
          },
          {
            title: "Sprechertrennung",
            description: "Agent und Kunde in jeder Antwort erkennen."
          },
          {
            title: "Sales-Quality-Analyse",
            description: "Fehler, verpasste Chancen und starke Momente erkennen."
          },
          {
            title: "Agenten-Reporting",
            description: "Scorecards und Coaching-Reports erstellen."
          }
        ],
        workflow: {
          title: "So funktioniert es",
          steps: [
            {
              title: "Aufnahmen importieren",
              description: "Mehrere Dateien auf einmal hochladen oder Speicher synchronisieren."
            },
            {
              title: "Transkribieren & markieren",
              description: "Whisper transkribiert und weist Agent/Kunde zu."
            },
            {
              title: "Gespräch analysieren",
              description: "KI markiert Risiken, verpasste Abschlüsse und überzeugende Formulierungen."
            },
            {
              title: "Berichten & coachen",
              description: "Reports und Aktionspläne pro Agent bereitstellen."
            }
          ]
        },
        outcomes: [
          {
            title: "Qualitäts-Scorecards",
            description: "Einheitliche Bewertung über Teams."
          },
          {
            title: "Hinweise auf verpasste Abschlüsse",
            description: "Sehen, wo Deals verloren gingen."
          },
          {
            title: "Gewinnende Playbooks",
            description: "Formulierungen sichern, die konvertieren."
          }
        ]
      },
      pricing: {
        title: "Einfaches, transparentes Pricing",
        subtitle: "Kostenlos starten, später upgraden.",
        popularLabel: "Beliebt",
        perMonth: "/Monat",
        plans: [
          {
            name: "Free",
            price: "$0",
            description: "Zum Ausprobieren von Hümo",
            features: [
              "100 Nachrichten/Monat",
              "Basis-KI-Analyse",
              "1 Integration",
              "E-Mail-Support"
            ]
          },
          {
            name: "Starter",
            price: "$29",
            description: "Für kleine Teams",
            popular: true,
            features: [
              "1.000 Nachrichten/Monat",
              "Erweiterte KI-Analyse",
              "3 Integrationen",
              "Auto-Antwort Vorschläge",
              "Priorisierter Support"
            ]
          },
          {
            name: "Pro",
            price: "$99",
            description: "Für wachsende Unternehmen",
            features: [
              "10.000 Nachrichten/Monat",
              "Echtzeit-Coach",
              "Unbegrenzte Integrationen",
              "Automatisierte Antworten",
              "Sales Intelligence",
              "Analytics Dashboard",
              "24/7 Support"
            ]
          },
          {
            name: "Growth",
            price: "Kontaktieren Sie uns",
            description: "Für Enterprise-Teams",
            features: [
              "Unbegrenzte Nachrichten",
              "Individuelles KI-Training",
              "White-Label Optionen",
              "API-Zugriff",
              "Dedizierter Account Manager",
              "SLA-Garantie"
            ]
          }
        ]
      },
      cta: {
        title: "Bereit für bessere Kundenkonversationen?",
        subtitle: "Schließen Sie sich Teams an, die mit Hümo wachsen.",
        primary: "Kostenlos starten",
        secondary: "Demo buchen"
      },
      howItWorks: {
        title: "In wenigen Minuten einsatzbereit",
        subtitle: "Kanäle verbinden, Wissensdatenbank hinzufügen — Hümo erledigt den Rest.",
        steps: [
          { number: "01", title: "Kanäle verbinden", description: "Instagram, WhatsApp, Telegram oder Web-Widget in unter 5 Minuten einrichten." },
          { number: "02", title: "Wissensdatenbank anlegen", description: "FAQs und Dokumente hochladen — Hümo lernt Ihr Unternehmen und antwortet automatisch." },
          { number: "03", title: "KI übernimmt die Arbeit", description: "Auto-Antworten, Coaching, Follow-ups, CSAT — Hümo verwaltet den Posteingang, Ihr Team schließt Deals." }
        ]
      },
      spotlights: [
        { badge: "AI Copilot", title: "Versandfertige Vorschläge per Klick", subtitle: "Hümo analysiert jeden Chat und zeigt direkt über dem Eingabefeld versandfertige Nachrichten an. Ihr Team arbeitet schneller.", items: ["Echtzeit-Vorschläge", "Einfügen, bearbeiten oder senden", "Lernt von den besten Agenten", "Funktioniert in allen Kanälen"] },
        { badge: "CSAT", title: "Messen Sie, was Kunden wirklich denken", subtitle: "Sammeln Sie automatisch Bewertungen und Kommentare nach jedem Gespräch. Trends erkennen und Team coachen.", items: ["Automatische Umfrage nach Abschluss", "Sternebewertung + Kommentar", "Echtzeit-Dashboard", "Filter nach Agent und Kanal"] },
        { badge: "Scheduled Messages", title: "Warme Leads nie erkalten lassen", subtitle: "Follow-ups planen, Inaktivitäts-Trigger setzen — Hümo kümmert sich automatisch um jeden Lead.", items: ["Nachrichten zeitlich planen", "Trigger bei Inaktivität", "Follow-up-Erinnerungen", "In allen Kanälen verfügbar"] }
      ]
    },
    featuresPage: {
      hero: {
        title: "Alle Funktionen für Verkauf und Support im großen Maßstab",
        subtitle: "Hümo kombiniert KI-Analyse, Automatisierung und Analytics in einer Plattform.",
        primaryCta: "Kostenlos starten",
        secondaryCta: "Preise ansehen"
      },
      highlights: [
        {
          title: "Intent & Stimmung",
          description: "Verstehen Sie, warum Kunden schreiben und wie man richtig antwortet."
        },
        {
          title: "Umsatz-Analytics",
          description: "Sehen Sie den Einfluss nach Kanal, Agent und Automatisierung."
        }
      ],
      capabilities: {
        title: "Plattform-Fähigkeiten",
        subtitle: "Vom Onboarding bis zum Reporting für vielbeschäftigte Teams."
      },
      trust: {
        title: "Vertrauen und Sicherheit",
        items: [
          { title: "GDPR-konform", description: "Datenschutz und Kontrolle als Standard." },
          { title: "Admin- und Agentenrollen", description: "Getrennte Rechte für Admins und Agenten." },
          { title: "Verschlüsselung bei der Übertragung", description: "Verbindungen laufen über HTTPS; Kanal-Zugangsdaten werden verschlüsselt gespeichert." }
        ]
      },
      cta: {
        title: "Hümo live erleben",
        subtitle: "Hümo hilft beim Start in wenigen Tagen.",
        primary: "Loslegen",
        secondary: "Sales kontaktieren"
      }
    },
    blogPage: {
      hero: {
        title: "Insights von Hümo",
        subtitle: "Produkt-Updates, KI-Forschung und Best Practices für Teams."
      },
      categories: ["Alle", "Produkt", "KI", "Sales", "CX"],
      posts: [
        {
          title: "Wie KI Kundenkonversationen verändert",
          excerpt: "Ein praktischer Blick auf Intent-Erkennung, Coaching und sichere Automatisierung.",
          category: "KI",
          date: "10. Jan 2026"
        },
        {
          title: "Von Antwortzeit zu Umsatz",
          excerpt: "Die Kennzahlen, die Pipeline und Retention wirklich bewegen.",
          category: "Sales",
          date: "4. Jan 2026"
        },
        {
          title: "Vertrauen in Auto-Antworten",
          excerpt: "Warum Guardrails, Review und Transparenz wichtig sind.",
          category: "CX",
          date: "20. Dez 2025"
        }
      ],
      newsletter: {
        title: "Updates erhalten",
        subtitle: "Monatliche Produktnews und KI-Playbooks.",
        placeholder: "you@company.com",
        button: "Abonnieren"
      }
    },
    aboutPage: {
      hero: {
        title: "Über Hümo",
        subtitle: "Wir bauen Hümo, damit Teams schneller und menschlicher arbeiten können."
      },
      mission: {
        title: "Mission",
        description: "Jede Kundenkonversation zu einer Wachstumschance machen."
      },
      vision: {
        title: "Vision",
        description: "Teams skalieren Care und Umsatz ohne Qualitätsverlust."
      },
      story: {
        title: "Unsere Geschichte",
        steps: [
          {
            title: "2019 — Die Idee",
            description: "Start als Research-Lab für Intent-Erkennung."
          },
          {
            title: "2022 — Hümo startet",
            description: "Frühe Kunden halbierten die Antwortzeit."
          },
          {
            title: "2026 — Globale Teams",
            description: "Hümo unterstützt Teams in Europa und dem Nahen Osten."
          }
        ]
      },
      values: {
        title: "Unsere Werte",
        items: [
          { title: "Kundenfokus", description: "Wir denken vom Kunden her." },
          { title: "Vertrauen", description: "Sicherheit und Transparenz sind Pflicht." },
          { title: "Tempo mit Sorgfalt", description: "Schnell handeln ohne menschliche Qualität zu verlieren." }
        ]
      },
      leadership: {
        title: "Leadership",
        people: []
      },
      stats: [
        { value: "24/7", label: "Sofortige Antworten" },
        { value: "TR + EN", label: "In der Sprache Ihrer Kunden" },
        { value: "3 Kanäle", label: "WhatsApp · Instagram · Web" }
      ],
      cta: {
        title: "Partner mit Hümo",
        subtitle: "Lassen Sie uns die nächste Generation von Kundenkonversationen bauen.",
        primary: "Kontakt",
        secondary: "Karriere"
      }
    },
    careersPage: {
      hero: {
        title: "Werde Teil von Hümo",
        subtitle: "Baue KI-Produkte für bessere Kundenerlebnisse.",
        primaryCta: "Offene Rollen",
        secondaryCta: "E-Mail senden"
      },
      benefits: {
        title: "Warum du hier gern arbeitest",
        items: [
          "Remote-first und flexible Zeiten",
          "Wettbewerbsfähiges Gehalt und Equity",
          "Budget für Lernen und Konferenzen",
          "Produktkultur mit Impact",
          "Moderne Tools und Support"
        ]
      },
      roles: {
        title: "Offene Positionen",
        items: [
          {
            title: "Frontend Engineer",
            location: "Remote · Europe",
            type: "Vollzeit",
            team: "Product",
            description: "Baue schnelle, hochwertige UI-Erlebnisse für Hümo."
          },
          {
            title: "Backend Engineer",
            location: "Remote · Turkey",
            type: "Vollzeit",
            team: "Platform",
            description: "Entwerfe skalierbare Systeme für Nachrichten und Analytics."
          },
          {
            title: "Machine Learning Engineer",
            location: "Remote · Europe",
            type: "Vollzeit",
            team: "AI",
            description: "Verbessere Modelle für Klassifikation und Automatisierung."
          },
          {
            title: "Customer Success Manager",
            location: "Hybrid · Istanbul",
            type: "Vollzeit",
            team: "Customer",
            description: "Hilf Teams, schnell zu starten und Ergebnisse zu erzielen."
          }
        ]
      },
      process: {
        title: "Bewerbungsprozess",
        steps: [
          { title: "Kennenlernen", description: "Intro und Erwartungen klären." },
          { title: "Fachgespräch", description: "Tiefes Interview zu Skills und Szenarien." },
          { title: "Finale Runde", description: "Culture Fit, Angebot und nächste Schritte." }
        ]
      },
      cta: {
        title: "Bereit für die Bewerbung?",
        subtitle: "Wir melden uns innerhalb von fünf Werktagen.",
        primary: "Jetzt bewerben",
        secondary: "careers@meethumo.com"
      }
    },
    helpPage: {
      hero: {
        title: "Hilfe-Center",
        subtitle: "Antworten, Tutorials und Best Practices für Hümo."
      },
      categories: {
        title: "Nach Themen",
        items: [
          { title: "Erste Schritte", description: "Hümo einrichten und Team einladen." },
          { title: "Integrationen", description: "Instagram, WhatsApp und Web-Chat verbinden." },
          { title: "Automatisierung", description: "Auto-Antworten und Routing-Regeln erstellen." },
          { title: "Analytics", description: "Conversions und Response-Metriken verfolgen." },
          { title: "Abrechnung", description: "Pläne, Rechnungen und Nutzung." }
        ]
      },
      articles: {
        title: "Beliebte Artikel",
        items: [
          "WhatsApp in 5 Minuten verbinden",
          "Confidence-Schwellen für Auto-Antworten",
          "Vorlagen erstellen und verwalten",
          "Analytics-Berichte exportieren"
        ]
      },
      faqs: {
        title: "Häufige Fragen",
        items: [
          {
            question: "Ersetzt Hümo meine Agenten?",
            answer: "Nein. Hümo unterstützt mit Analyse und Vorschlägen für schnellere Antworten."
          },
          {
            question: "Kann ich Auto-Senden kontrollieren?",
            answer: "Ja. Setzen Sie Vertrauensschwellen und optional menschliche Freigaben."
          },
          {
            question: "Welche Kanäle werden unterstützt?",
            answer: "Instagram, WhatsApp, Web-Chat und Voice werden aktuell unterstützt."
          },
          {
            question: "Wie schnell sind wir live?",
            answer: "Die meisten Teams sind in weniger als einer Woche live."
          }
        ]
      },
      contact: {
        title: "Noch Hilfe nötig?",
        subtitle: "Unser Support ist auf Englisch und Türkisch verfügbar.",
        primary: "Support kontaktieren",
        secondary: "support@meethumo.com"
      }
    },
    footer: {
      tagline: "KI-gestützte Kundenintelligenz für moderne Teams.",
      product: "Produkt",
      company: "Unternehmen",
      support: "Support",
      links: {
        features: "Funktionen",
        blog: "Blog",
        about: "Über uns",
        careers: "Karriere",
        helpCenter: "Hilfe-Center",
        pricing: "Preise"
      },
      rights: "© 2026 Hümo. Alle Rechte vorbehalten."
    }
  },
  fr: {
    brand: {
      product: "Hümo",
      company: "Hümo"
    },
    nav: {
      features: "Fonctionnalités",
      blog: "Blog",
      about: "À propos",
      careers: "Carrières",
      helpCenter: "Centre d'aide",
      signIn: "Se connecter",
      getStarted: "Commencer"
    },
    landing: {
      hero: {
        badge: "Intelligence client alimentée par l'IA",
        title: "Transformez les messages clients en",
        highlight: "Revenus",
        subtitle:
          "Analyse IA, coaching en temps réel et automatisation intelligente pour aider les équipes à conclure plus de ventes.",
        primaryCta: "Essai gratuit",
        secondaryCta: "Voir la démo"
      },
      proof: ["Sans carte bancaire", "Essai gratuit de 14 jours", "Annulation à tout moment"],
      previewLabel: "Aperçu du dashboard",
      stats: [
        { value: "24/7", label: "Réponses instantanées" },
        { value: "TR + EN", label: "Dans la langue du client" },
        { value: "3 canaux", label: "WhatsApp · Instagram · Web" }
      ],
      features: {
        title: "Tout ce qu'il faut pour scaler les conversations",
        subtitle: "Des fonctions IA puissantes pour répondre plus vite et vendre davantage.",
        items: [
          {
            title: "Analyse IA",
            description: "Classification automatique par intention, sentiment et urgence."
          },
          {
            title: "Réponses automatiques intelligentes",
            description: "Réponses contextuelles avec seuils de confiance et garde‑fous."
          },
          {
            title: "Coach en temps réel",
            description: "Coaching IA en direct pour aider vos agents."
          },
          {
            title: "Sales intelligence",
            description: "Suivez l'intention d'achat et les étapes de conversation."
          },
          {
            title: "Boîte de réception multicanale",
            description: "Instagram, WhatsApp, chat web et messages vocaux unifiés."
          },
          {
            title: "Tableau de bord Analytics",
            description: "Métriques, entonnoirs et performance des agents."
          }
        ]
      },
      integrations: {
        title: "Connectez tous vos canaux",
        subtitle: "Une seule boîte de réception pour toutes les conversations.",
        items: [
          { name: "Instagram" },
          { name: "WhatsApp" },
          { name: "Chat Web" },
          { name: "Voix" }
        ]
      },
      useCases: {
        title: "Conçu pour les équipes performantes",
        items: [
          {
            title: "Support e‑commerce",
            description: "Gérez commandes, retours et questions automatiquement."
          },
          {
            title: "Équipes commerciales",
            description: "Coaching et signaux d'intention pour conclure plus vite."
          },
          {
            title: "Customer Success",
            description: "Identifiez les risques et opportunités d'upsell."
          }
        ]
      },
      enterprise: {
        badge: "Intelligence vocale enterprise",
        title: "Transformez les appels enregistrés en actions",
        subtitle:
          "Importez des lots d'appels, transcrivez et séparez les intervenants, puis analysez les erreurs, les ventes manquées et les phrases convaincantes.",
        highlights: [
          {
            title: "Import en lot",
            description: "Chargez des enregistrements par lots et traitez-les automatiquement."
          },
          {
            title: "Séparation des intervenants",
            description: "Identifiez agent et client à chaque échange."
          },
          {
            title: "Analyse qualité des ventes",
            description: "Repérez erreurs, opportunités manquées et moments forts."
          },
          {
            title: "Reporting des agents",
            description: "Générez des scorecards et des rapports de coaching."
          }
        ],
        workflow: {
          title: "Comment ça marche",
          steps: [
            {
              title: "Importer les enregistrements",
              description: "Téléversez plusieurs fichiers ou synchronisez le stockage."
            },
            {
              title: "Transcrire et taguer",
              description: "Whisper transcrit et attribue les rôles agent/client."
            },
            {
              title: "Analyser la conversation",
              description: "L'IA met en évidence risques, ventes manquées et phrases convaincantes."
            },
            {
              title: "Reporter et coacher",
              description: "Diffusez des rapports et plans d'action pour chaque agent."
            }
          ]
        },
        outcomes: [
          {
            title: "Scorecards qualité",
            description: "Standardiser l'évaluation."
          },
          {
            title: "Alertes ventes manquées",
            description: "Voir où les deals ont glissé."
          },
          {
            title: "Playbooks gagnants",
            description: "Capturer les phrases qui convertissent."
          }
        ]
      },
      pricing: {
        title: "Tarifs simples et transparents",
        subtitle: "Commencez gratuitement, évoluez ensuite.",
        popularLabel: "Le plus populaire",
        perMonth: "/mois",
        plans: [
          {
            name: "Gratuit",
            price: "$0",
            description: "Pour essayer Hümo",
            features: [
              "100 messages/mois",
              "Analyse IA basique",
              "1 intégration",
              "Support email"
            ]
          },
          {
            name: "Starter",
            price: "$29",
            description: "Pour les petites équipes",
            popular: true,
            features: [
              "1 000 messages/mois",
              "Analyse IA avancée",
              "3 intégrations",
              "Suggestions de réponses",
              "Support prioritaire"
            ]
          },
          {
            name: "Pro",
            price: "$99",
            description: "Pour les entreprises en croissance",
            features: [
              "10 000 messages/mois",
              "Coach IA en temps réel",
              "Intégrations illimitées",
              "Automatisation des réponses",
              "Sales intelligence",
              "Analytics dashboard",
              "Support 24/7"
            ]
          },
          {
            name: "Growth",
            price: "Contactez-nous",
            description: "Pour les entreprises à grande échelle",
            features: [
              "Messages illimités",
              "Entraînement IA sur mesure",
              "Options white‑label",
              "Accès API",
              "Account manager dédié",
              "Garantie SLA"
            ]
          }
        ]
      },
      cta: {
        title: "Prêt à transformer vos conversations clients ?",
        subtitle: "Rejoignez les équipes qui grandissent avec Hümo.",
        primary: "Essai gratuit",
        secondary: "Planifier une démo"
      },
      howItWorks: {
        title: "Opérationnel en quelques minutes",
        subtitle: "Connectez vos canaux, ajoutez votre base de connaissances — Hümo fait le reste.",
        steps: [
          { number: "01", title: "Connectez vos canaux", description: "Instagram, WhatsApp, Telegram ou widget web en moins de 5 minutes." },
          { number: "02", title: "Ajoutez votre base de connaissances", description: "Téléchargez vos FAQ et docs — Hümo apprend votre entreprise et répond automatiquement." },
          { number: "03", title: "L'IA prend en charge", description: "Réponses auto, coaching, suivi, CSAT — Hümo gère votre boîte de réception." }
        ]
      },
      spotlights: [
        { badge: "AI Copilot", title: "Suggestions prêtes à envoyer en un clic", subtitle: "Hümo analyse chaque conversation et affiche des suggestions au-dessus du compositeur. Votre équipe travaille plus vite.", items: ["Suggestions en temps réel", "Insérer, modifier ou envoyer", "Apprend des meilleurs agents", "Fonctionne sur tous les canaux"] },
        { badge: "CSAT", title: "Mesurez ce que pensent vraiment vos clients", subtitle: "Collectez automatiquement des évaluations et commentaires après chaque conversation. Suivez les tendances et coachez votre équipe.", items: ["Enquête automatique après clôture", "Étoiles + commentaire facultatif", "Tableau de bord en temps réel", "Filtrer par agent et canal"] },
        { badge: "Scheduled Messages", title: "Ne laissez jamais un lead refroidir", subtitle: "Planifiez des suivis, définissez des déclencheurs d'inactivité — Hümo s'occupe automatiquement de chaque lead.", items: ["Messages planifiés", "Déclencheur d'inactivité", "Rappels de suivi", "Fonctionne sur tous les canaux"] }
      ]
    },
    featuresPage: {
      hero: {
        title: "Toutes les fonctionnalités pour vendre et soutenir à grande échelle",
        subtitle: "Hümo réunit analyse IA, automatisation et analytics dans une seule plateforme.",
        primaryCta: "Essai gratuit",
        secondaryCta: "Voir les prix"
      },
      highlights: [
        {
          title: "Intention & sentiment",
          description: "Comprenez pourquoi les clients écrivent et répondez avec le bon ton."
        },
        {
          title: "Analytics revenue",
          description: "Mesurez l'impact par canal, agent et automatisation."
        }
      ],
      capabilities: {
        title: "Capacités de la plateforme",
        subtitle: "De l'onboarding aux rapports, pensé pour les équipes occupées."
      },
      trust: {
        title: "Confiance et sécurité",
        items: [
          { title: "Conforme au RGPD", description: "Confidentialité et contrôle des données par défaut." },
          { title: "Rôles administrateur et agent", description: "Des droits distincts pour les administrateurs et les agents." },
          { title: "Chiffrement en transit", description: "Les connexions passent par HTTPS ; les identifiants des canaux sont stockés chiffrés." }
        ]
      },
      cta: {
        title: "Découvrez Hümo",
        subtitle: "Hümo vous aide à déployer en quelques jours.",
        primary: "Commencer",
        secondary: "Contacter les ventes"
      }
    },
    blogPage: {
      hero: {
        title: "Insights de Hümo",
        subtitle: "Mises à jour produit, recherche IA et bonnes pratiques."
      },
      categories: ["Tous", "Produit", "IA", "Ventes", "CX"],
      posts: [
        {
          title: "Comment l'IA change les conversations clients",
          excerpt: "Intentions, coaching et automatisation en pratique.",
          category: "IA",
          date: "10 janv. 2026"
        },
        {
          title: "De la vitesse de réponse au revenu",
          excerpt: "Les métriques qui impactent vraiment la croissance.",
          category: "Ventes",
          date: "4 janv. 2026"
        },
        {
          title: "Construire la confiance dans les réponses automatiques",
          excerpt: "Pourquoi les garde‑fous et la transparence sont essentiels.",
          category: "CX",
          date: "20 déc. 2025"
        }
      ],
      newsletter: {
        title: "Recevez nos mises à jour",
        subtitle: "Actualités produit et playbooks IA chaque mois.",
        placeholder: "you@company.com",
        button: "S'abonner"
      }
    },
    aboutPage: {
      hero: {
        title: "À propos de Hümo",
        subtitle: "Nous créons Hümo pour des expériences clients plus rapides et plus humaines."
      },
      mission: {
        title: "Mission",
        description: "Transformer chaque conversation client en opportunité de croissance grâce à l'IA."
      },
      vision: {
        title: "Vision",
        description: "Permettre aux équipes de scaler le care et le revenu sans perdre en qualité."
      },
      story: {
        title: "Notre histoire",
        steps: [
          {
            title: "2019 — L'idée",
            description: "Un laboratoire de recherche sur la détection d'intention."
          },
          {
            title: "2022 — Lancement d'Hümo",
            description: "Les premiers clients ont réduit le temps de réponse de moitié."
          },
          {
            title: "2026 — Équipes internationales",
            description: "Hümo accompagne des équipes en Europe et au Moyen‑Orient."
          }
        ]
      },
      values: {
        title: "Nos valeurs",
        items: [
          { title: "Obsédé par le client", description: "Nous partons des besoins clients." },
          { title: "Confiance", description: "Sécurité et transparence avant tout." },
          { title: "Vitesse avec soin", description: "Aller vite sans casser l'expérience humaine." }
        ]
      },
      leadership: {
        title: "Leadership",
        people: []
      },
      stats: [
        { value: "24/7", label: "Réponses instantanées" },
        { value: "TR + EN", label: "Dans la langue du client" },
        { value: "3 canaux", label: "WhatsApp · Instagram · Web" }
      ],
      cta: {
        title: "Partenariat avec Hümo",
        subtitle: "Construisons ensemble la nouvelle génération de conversations clients.",
        primary: "Nous contacter",
        secondary: "Carrières"
      }
    },
    careersPage: {
      hero: {
        title: "Rejoignez Hümo",
        subtitle: "Créez des produits IA pour de meilleures expériences clients.",
        primaryCta: "Voir les postes",
        secondaryCta: "Envoyer un email"
      },
      benefits: {
        title: "Pourquoi travailler ici",
        items: [
          "Télétravail et horaires flexibles",
          "Rémunération compétitive",
          "Budget formation et conférences",
          "Culture produit à fort impact",
          "Outils modernes et équipes bienveillantes"
        ]
      },
      roles: {
        title: "Postes ouverts",
        items: [
          {
            title: "Frontend Engineer",
            location: "Remote · Europe",
            type: "Temps plein",
            team: "Product",
            description: "Construisez des interfaces rapides et élégantes pour Hümo."
          },
          {
            title: "Backend Engineer",
            location: "Remote · Turkey",
            type: "Temps plein",
            team: "Platform",
            description: "Concevez des systèmes scalables pour le messaging et l'analytics."
          },
          {
            title: "Machine Learning Engineer",
            location: "Remote · Europe",
            type: "Temps plein",
            team: "AI",
            description: "Améliorez les modèles de classification et d'automatisation."
          },
          {
            title: "Customer Success Manager",
            location: "Hybrid · Istanbul",
            type: "Temps plein",
            team: "Customer",
            description: "Aidez les équipes à se lancer vite et à obtenir des résultats."
          }
        ]
      },
      process: {
        title: "Processus de recrutement",
        steps: [
          { title: "Premier échange", description: "Alignement sur le rôle et les attentes." },
          { title: "Entretien métier", description: "Focus sur vos compétences et cas réels." },
          { title: "Dernier entretien", description: "Culture, offre et prochaines étapes." }
        ]
      },
      cta: {
        title: "Prêt à postuler ?",
        subtitle: "Nous répondons sous cinq jours ouvrés.",
        primary: "Postuler",
        secondary: "careers@meethumo.com"
      }
    },
    helpPage: {
      hero: {
        title: "Centre d'aide",
        subtitle: "Réponses, tutoriels et meilleures pratiques pour Hümo."
      },
      categories: {
        title: "Parcourir par thème",
        items: [
          { title: "Démarrage", description: "Configurez Hümo et invitez votre équipe." },
          { title: "Intégrations", description: "Connectez Instagram, WhatsApp et le chat web." },
          { title: "Automatisation", description: "Créez des règles d'auto-réponse et de routage." },
          { title: "Analytics", description: "Suivez les conversions et les temps de réponse." },
          { title: "Facturation", description: "Plans, factures et usage." }
        ]
      },
      articles: {
        title: "Articles populaires",
        items: [
          "Connecter WhatsApp en 5 minutes",
          "Définir les seuils de confiance",
          "Créer et gérer des modèles",
          "Exporter les rapports analytics"
        ]
      },
      faqs: {
        title: "Questions fréquentes",
        items: [
          {
            question: "Hümo remplace-t-il mes agents ?",
            answer: "Non. Hümo assiste votre équipe avec l'analyse et des suggestions."
          },
          {
            question: "Puis-je contrôler l'envoi automatique ?",
            answer: "Oui. Définissez des seuils de confiance et des validations humaines."
          },
          {
            question: "Quels canaux sont pris en charge ?",
            answer: "Instagram, WhatsApp, chat web et messages vocaux sont disponibles."
          },
          {
            question: "Combien de temps pour être opérationnel ?",
            answer: "La plupart des équipes sont opérationnelles en moins d'une semaine."
          }
        ]
      },
      contact: {
        title: "Besoin d'aide supplémentaire ?",
        subtitle: "Notre support vous aide en anglais et en turc.",
        primary: "Contacter le support",
        secondary: "support@meethumo.com"
      }
    },
    footer: {
      tagline: "Plateforme d'intelligence client alimentée par l'IA.",
      product: "Produit",
      company: "Entreprise",
      support: "Support",
      links: {
        features: "Fonctionnalités",
        blog: "Blog",
        about: "À propos",
        careers: "Carrières",
        helpCenter: "Centre d'aide",
        pricing: "Tarifs"
      },
      rights: "© 2026 Hümo. Tous droits réservés."
    }
  },
  it: {
    brand: {
      product: "Hümo",
      company: "Hümo"
    },
    nav: {
      features: "Funzionalità",
      blog: "Blog",
      about: "Chi siamo",
      careers: "Carriere",
      helpCenter: "Centro assistenza",
      signIn: "Accedi",
      getStarted: "Inizia"
    },
    landing: {
      hero: {
        badge: "Intelligenza clienti basata su IA",
        title: "Trasforma i messaggi dei clienti in",
        highlight: "Fatturato",
        subtitle:
          "Analisi IA, coaching in tempo reale e automazione intelligente per aiutare i team a chiudere più trattative.",
        primaryCta: "Prova gratuita",
        secondaryCta: "Guarda la demo"
      },
      proof: ["Nessuna carta richiesta", "Prova gratuita di 14 giorni", "Annulla in qualsiasi momento"],
      previewLabel: "Anteprima dashboard",
      stats: [
        { value: "24/7", label: "Risposte immediate" },
        { value: "TR + EN", label: "Nella lingua del cliente" },
        { value: "3 canali", label: "WhatsApp · Instagram · Web" }
      ],
      features: {
        title: "Tutto ciò che serve per scalare le conversazioni",
        subtitle: "Funzionalità IA potenti per rispondere più velocemente e vendere di più.",
        items: [
          {
            title: "Analisi IA",
            description: "Classificazione automatica per intento, sentiment e urgenza."
          },
          {
            title: "Risposte automatiche intelligenti",
            description: "Risposte contestuali con soglie di confidenza e guardrail."
          },
          {
            title: "Coach in tempo reale",
            description: "Coaching IA live durante la conversazione."
          },
          {
            title: "Sales intelligence",
            description: "Traccia intenzione d'acquisto e fasi di conversazione."
          },
          {
            title: "Inbox multi‑canale",
            description: "Instagram, WhatsApp, chat web e messaggi vocali in un unico luogo."
          },
          {
            title: "Dashboard analytics",
            description: "Metriche, funnel e performance degli agenti."
          }
        ]
      },
      integrations: {
        title: "Connetti tutti i canali",
        subtitle: "Un'unica inbox per tutte le conversazioni con i clienti.",
        items: [
          { name: "Instagram" },
          { name: "WhatsApp" },
          { name: "Chat Web" },
          { name: "Voce" }
        ]
      },
      useCases: {
        title: "Pensato per team ad alte prestazioni",
        items: [
          {
            title: "Supporto e‑commerce",
            description: "Gestisci ordini, resi e richieste in modo automatico."
          },
          {
            title: "Team sales",
            description: "Coaching e segnali di intento per chiudere più velocemente."
          },
          {
            title: "Customer Success",
            description: "Individua rischi e opportunità di upsell con l'analisi del sentiment."
          }
        ]
      },
      enterprise: {
        badge: "Intelligenza vocale enterprise",
        title: "Trasforma le chiamate registrate in azioni",
        subtitle:
          "Carica in blocco le registrazioni, trascrivi e separa gli interlocutori, poi analizza errori, vendite perse e frasi persuasive.",
        highlights: [
          {
            title: "Importazione audio in blocco",
            description: "Carica lotti di registrazioni e processa automaticamente."
          },
          {
            title: "Separazione speaker",
            description: "Identifica agente e cliente in ogni turno."
          },
          {
            title: "Analisi qualità vendite",
            description: "Individua errori, opportunità mancate e momenti vincenti."
          },
          {
            title: "Reporting agenti",
            description: "Crea scorecard e report per il coaching."
          }
        ],
        workflow: {
          title: "Come funziona",
          steps: [
            {
              title: "Importa registrazioni",
              description: "Carica più file insieme o sincronizza lo storage."
            },
            {
              title: "Trascrivi ed etichetta",
              description: "Whisper trascrive e assegna ruoli agente/cliente."
            },
            {
              title: "Analizza la conversazione",
              description: "L'IA evidenzia rischi, vendite perse e frasi persuasive."
            },
            {
              title: "Report e coaching",
              description: "Consegna report e piani d'azione per ogni agente."
            }
          ]
        },
        outcomes: [
          {
            title: "Scorecard di qualità",
            description: "Standardizza la valutazione."
          },
          {
            title: "Avvisi vendite perse",
            description: "Vedi dove le trattative sono saltate."
          },
          {
            title: "Playbook vincenti",
            description: "Cattura le frasi che convertono."
          }
        ]
      },
      pricing: {
        title: "Prezzi semplici e trasparenti",
        subtitle: "Inizia gratis, poi aggiorna quando cresci.",
        popularLabel: "Più popolare",
        perMonth: "/mese",
        plans: [
          {
            name: "Gratis",
            price: "$0",
            description: "Perfetto per provare Hümo",
            features: [
              "100 messaggi/mese",
              "Analisi IA di base",
              "1 integrazione",
              "Supporto email"
            ]
          },
          {
            name: "Starter",
            price: "$29",
            description: "Per piccoli team",
            popular: true,
            features: [
              "1.000 messaggi/mese",
              "Analisi IA avanzata",
              "3 integrazioni",
              "Suggerimenti di risposta",
              "Supporto prioritario"
            ]
          },
          {
            name: "Pro",
            price: "$99",
            description: "Per aziende in crescita",
            features: [
              "10.000 messaggi/mese",
              "Coach IA in tempo reale",
              "Integrazioni illimitate",
              "Automazione risposte",
              "Sales intelligence",
              "Dashboard analytics",
              "Supporto 24/7"
            ]
          },
          {
            name: "Growth",
            price: "Contattaci",
            description: "Per aziende in scala",
            features: [
              "Messaggi illimitati",
              "Training IA personalizzato",
              "Opzioni white‑label",
              "Accesso API",
              "Account manager dedicato",
              "Garanzia SLA"
            ]
          }
        ]
      },
      cta: {
        title: "Pronto a trasformare le conversazioni con i clienti?",
        subtitle: "Unisciti ai team che crescono con Hümo.",
        primary: "Prova gratuita",
        secondary: "Prenota una demo"
      },
      howItWorks: {
        title: "Operativo in pochi minuti",
        subtitle: "Connetti i canali, aggiungi la knowledge base — Hümo farà il resto.",
        steps: [
          { number: "01", title: "Connetti i canali", description: "Instagram, WhatsApp, Telegram o widget web in meno di 5 minuti." },
          { number: "02", title: "Aggiungi la knowledge base", description: "Carica FAQ e documenti — Hümo impara la tua attività e risponde automaticamente." },
          { number: "03", title: "L'IA fa il lavoro pesante", description: "Risposte automatiche, coaching, follow-up, CSAT — Hümo gestisce la tua inbox." }
        ]
      },
      spotlights: [
        { badge: "AI Copilot", title: "Suggerimenti pronti all'invio con un clic", subtitle: "Hümo analizza ogni conversazione e mostra suggerimenti sopra il compositore. Il tuo team lavora più velocemente.", items: ["Suggerimenti in tempo reale", "Inserisci, modifica o invia", "Impara dai migliori agenti", "Funziona su tutti i canali"] },
        { badge: "CSAT", title: "Misura cosa pensano davvero i clienti", subtitle: "Raccogli automaticamente valutazioni e commenti dopo ogni conversazione. Monitora i trend e guida il team.", items: ["Sondaggio automatico dopo chiusura", "Stelle + commento opzionale", "Dashboard in tempo reale", "Filtra per agente e canale"] },
        { badge: "Scheduled Messages", title: "Non lasciare raffreddare i lead", subtitle: "Pianifica follow-up, imposta trigger di inattività — Hümo si occupa automaticamente di ogni lead.", items: ["Messaggi programmati", "Trigger per inattività", "Promemoria di follow-up", "Funziona su tutti i canali"] }
      ]
    },
    featuresPage: {
      hero: {
        title: "Tutte le funzionalità per vendite e supporto su larga scala",
        subtitle: "Hümo unisce analisi IA, automazione e analytics in un'unica piattaforma.",
        primaryCta: "Prova gratuita",
        secondaryCta: "Vedi prezzi"
      },
      highlights: [
        {
          title: "Intento e sentiment",
          description: "Comprendi perché i clienti scrivono e rispondi con il tono giusto."
        },
        {
          title: "Analytics del fatturato",
          description: "Misura l'impatto per canale, agente e automazione."
        }
      ],
      capabilities: {
        title: "Capacità della piattaforma",
        subtitle: "Dall'onboarding al reporting, pensato per team impegnati."
      },
      trust: {
        title: "Affidabilità e sicurezza",
        items: [
          { title: "Conforme GDPR", description: "Privacy e controllo dati per impostazione predefinita." },
          { title: "Ruoli amministratore e agente", description: "Permessi separati per amministratori e agenti." },
          { title: "Crittografia in transito", description: "Le connessioni usano HTTPS; le credenziali dei canali sono salvate cifrate." }
        ]
      },
      cta: {
        title: "Scopri Hümo",
        subtitle: "Hümo ti aiuta a lanciare in pochi giorni.",
        primary: "Inizia",
        secondary: "Contatta vendite"
      }
    },
    blogPage: {
      hero: {
        title: "Insight di Hümo",
        subtitle: "Aggiornamenti prodotto, ricerca IA e best practice per i team."
      },
      categories: ["Tutti", "Prodotto", "IA", "Vendite", "CX"],
      posts: [
        {
          title: "Come l'IA cambia le conversazioni con i clienti",
          excerpt: "Intento, coaching e automazione sicura in pratica.",
          category: "IA",
          date: "10 gen 2026"
        },
        {
          title: "Dalla velocità di risposta al fatturato",
          excerpt: "Le metriche che muovono davvero pipeline e retention.",
          category: "Vendite",
          date: "4 gen 2026"
        },
        {
          title: "Costruire fiducia nelle risposte automatiche",
          excerpt: "Perché guardrail e trasparenza sono fondamentali.",
          category: "CX",
          date: "20 dic 2025"
        }
      ],
      newsletter: {
        title: "Ricevi gli aggiornamenti",
        subtitle: "Novità di prodotto e playbook IA ogni mese.",
        placeholder: "you@company.com",
        button: "Iscriviti"
      }
    },
    aboutPage: {
      hero: {
        title: "Chi è Hümo",
        subtitle: "Costruiamo Hümo per esperienze clienti più rapide e umane."
      },
      mission: {
        title: "Missione",
        description: "Trasformare ogni conversazione in opportunità di crescita con l'IA."
      },
      vision: {
        title: "Visione",
        description: "Un mondo in cui i team scalano cura e ricavi senza perdere qualità."
      },
      story: {
        title: "La nostra storia",
        steps: [
          {
            title: "2019 — L'idea",
            description: "Un laboratorio di ricerca sull'intent detection per aziende reali."
          },
          {
            title: "2022 — Lancio di Hümo",
            description: "I primi clienti hanno dimezzato i tempi di risposta."
          },
          {
            title: "2026 — Team globali",
            description: "Hümo supporta team in Europa e Medio Oriente."
          }
        ]
      },
      values: {
        title: "I nostri valori",
        items: [
          { title: "Centralità del cliente", description: "Progettiamo partendo dal cliente." },
          { title: "Fiducia", description: "Sicurezza e trasparenza sono essenziali." },
          { title: "Velocità con cura", description: "Muoversi veloce senza perdere l'umanità." }
        ]
      },
      leadership: {
        title: "Leadership",
        people: []
      },
      stats: [
        { value: "24/7", label: "Risposte immediate" },
        { value: "TR + EN", label: "Nella lingua del cliente" },
        { value: "3 canali", label: "WhatsApp · Instagram · Web" }
      ],
      cta: {
        title: "Collabora con Hümo",
        subtitle: "Costruiamo insieme la nuova generazione di conversazioni clienti.",
        primary: "Contattaci",
        secondary: "Carriere"
      }
    },
    careersPage: {
      hero: {
        title: "Unisciti a Hümo",
        subtitle: "Costruisci prodotti IA per migliori esperienze clienti.",
        primaryCta: "Ruoli aperti",
        secondaryCta: "Scrivici"
      },
      benefits: {
        title: "Perché lavorare con noi",
        items: [
          "Remote-first e orari flessibili",
          "Retribuzione competitiva",
          "Budget formazione e conferenze",
          "Cultura prodotto ad alto impatto",
          "Strumenti moderni e team di supporto"
        ]
      },
      roles: {
        title: "Posizioni aperte",
        items: [
          {
            title: "Frontend Engineer",
            location: "Remote · Europe",
            type: "Full-time",
            team: "Product",
            description: "Costruisci interfacce veloci e curate per Hümo."
          },
          {
            title: "Backend Engineer",
            location: "Remote · Turkey",
            type: "Full-time",
            team: "Platform",
            description: "Progetta sistemi scalabili per messaggi e analytics."
          },
          {
            title: "Machine Learning Engineer",
            location: "Remote · Europe",
            type: "Full-time",
            team: "AI",
            description: "Migliora i modelli di classificazione e automazione."
          },
          {
            title: "Customer Success Manager",
            location: "Hybrid · Istanbul",
            type: "Full-time",
            team: "Customer",
            description: "Aiuta i team a partire velocemente e ottenere risultati."
          }
        ]
      },
      process: {
        title: "Processo di selezione",
        steps: [
          { title: "Intro", description: "Conosci il team e allinea le aspettative." },
          { title: "Colloquio tecnico", description: "Approfondimento su competenze e casi reali." },
          { title: "Finale", description: "Culture fit, offerta e prossimi passi." }
        ]
      },
      cta: {
        title: "Pronto a candidarti?",
        subtitle: "Rispondiamo a ogni candidatura entro 5 giorni lavorativi.",
        primary: "Candidati",
        secondary: "careers@meethumo.com"
      }
    },
    helpPage: {
      hero: {
        title: "Centro assistenza",
        subtitle: "Risposte, tutorial e best practice per Hümo."
      },
      categories: {
        title: "Sfoglia per argomento",
        items: [
          { title: "Primi passi", description: "Configura Hümo e invita il team." },
          { title: "Integrazioni", description: "Connetti Instagram, WhatsApp e chat web." },
          { title: "Automazione", description: "Crea auto-reply e regole di routing." },
          { title: "Analytics", description: "Traccia conversioni e tempi di risposta." },
          { title: "Fatturazione", description: "Piani, fatture e utilizzo." }
        ]
      },
      articles: {
        title: "Articoli popolari",
        items: [
          "Connettere WhatsApp in 5 minuti",
          "Impostare le soglie di confidenza",
          "Creare e gestire template",
          "Esportare report analytics"
        ]
      },
      faqs: {
        title: "Domande frequenti",
        items: [
          {
            question: "Hümo sostituisce i miei agenti?",
            answer: "No. Hümo aiuta il team con analisi e suggerimenti per rispondere più rapidamente."
          },
          {
            question: "Posso controllare l'invio automatico?",
            answer: "Sì. Puoi impostare soglie di confidenza e richiedere revisione umana."
          },
          {
            question: "Quali canali sono supportati?",
            answer: "Instagram, WhatsApp, chat web e voice sono supportati oggi."
          },
          {
            question: "Quanto tempo serve per andare live?",
            answer: "La maggior parte dei team è live in meno di una settimana."
          }
        ]
      },
      contact: {
        title: "Hai ancora bisogno di aiuto?",
        subtitle: "Il nostro supporto è disponibile in inglese e turco.",
        primary: "Contatta il supporto",
        secondary: "support@meethumo.com"
      }
    },
    footer: {
      tagline: "Piattaforma di customer intelligence basata su IA per team moderni.",
      product: "Prodotto",
      company: "Azienda",
      support: "Supporto",
      links: {
        features: "Funzionalità",
        blog: "Blog",
        about: "Chi siamo",
        careers: "Carriere",
        helpCenter: "Centro assistenza",
        pricing: "Prezzi"
      },
      rights: "© 2026 Hümo. Tutti i diritti riservati."
    }
  }
};

export const getMarketingCopy = (language: string): MarketingCopy => {
  return marketingCopy[language as MarketingLanguage] || marketingCopy.en;
};
